import "isomorphic-fetch";
import {
  Client,
  BatchRequestStep,
  BatchRequestContent,
  BatchResponseContent,
  GraphRequest,
  PageIteratorCallback,
  PageIterator,
  ResponseType,
  LargeFileUploadSession,
  LargeFileUploadTask,
  FileUpload,
  GraphError,
} from "@microsoft/microsoft-graph-client/lib/src/browser";
import { AxiosResponse } from "axios";
import { IDriveInfo } from "../interfaces/IDriveInfo";
import { ITeamChannel, ITeamChannelSite } from "../interfaces/services/ITeamChannel";
import { ITeamChannelDto } from "../interfaces/services/ITeamChannelDto";
import { ITeamChannelDocumentLibrary } from "../interfaces/services/ITeamChannelDocumentLibrary";
import { ITeam } from "../interfaces/services/ITeam";
import { filters, initialGraphSearchPager, microsoftGraph, searchFields, siteQueries } from "../config/constants";
import { GraphSearchRestHelper } from "../components/helpers/restSearchBuilder";
import { SearchResultMapper } from "../components/helpers/searchResultMapper";
import { ISearchDocumentLibraryParams } from "../interfaces/ISearchDocumentLibraryParams";
import { SearchBatchRequestHelper } from "../components/helpers/searchBatchRequestHelper";
import { DriveItemSearchResult } from "../interfaces/IDriveItemSearchResult";
import { DriveItemSearchResultComposite } from "../interfaces/DriveItemSearchResultComposite";
import { IBatchSearchResult } from "../interfaces/IBatchSearchResult";
import { DriveIdBatchHelper, siteWithoutAnswer } from "../components/helpers/driveIdBatchHelper";
import { ISiteBatchInfo } from "../interfaces/ISiteBatchInfo";
import { ISiteDriveItems } from "../interfaces/ISiteDriveItems";
import { ISharepointDocument } from "../interfaces/ISharepointDocument";
import { IDefaultSite } from "../interfaces/IDefaultSite";
import { IDriveItemShareableLink } from "../interfaces/IDriveItemShareableLink";
import { IShareableLink } from "../interfaces/services/IShareableLink";
import authModule, { TokenResource } from "../../../utilities/AuthModule";
import { isOutageFailure } from "../../../utilities/authRejection";
import { IFileFolderSearch } from "../interfaces/IFileFolderSearch";
import { IFileFolderSearchSharepointParams } from "../interfaces/IFileFolderSearchSharepointParams";
import { IBetaSearchExtraDriveInfo } from "../interfaces/IBetaSearchExtraDriveInfo";
import { getExtension } from "../components/helpers/stringHelper";
import { isSystemLibrary } from "../components/helpers/systemLibraryHelper";
import { IGraphPager } from "../interfaces/IGraphPager";
import { IFolderDrivePaging } from "../interfaces/IFolderDrivePaging";
import { ISharepointFolderDrivePaging } from "../interfaces/ISharepointFolderDrivePaging";
import path = require("path");
import { IMapper } from "../interfaces/IMapper";
import { ISharepointRelativeFileInfo } from "../interfaces/ISharepointRelativeFileInfo";
import { IGraphSearchResult } from "../interfaces/IGraphSearchResult";
import { ITeamChannelInfo } from "../interfaces/ITeamChannelInfo";
import { IDriveItemRequest } from "../interfaces/IDriveItemRequest";
import { IProtectionLabel } from "../interfaces/IProtectionLabel";
import { logger } from "../../../utilities/logger";
import { axiosRetryGet, axiosRetryPost } from "../../../utilities/AxiosRetryUtil";
import { ISiteInfo, ISiteListLoad, ISiteSearchRequest, ISiteSearchResult, ISubSiteInfo, filterNonTopLevelSites, mapSites, mapSubSites } from "../interfaces/ISiteInfo";
import { IReassignPerson } from "../components/directoryComponents/myApprovals/IReassignPerson";
import { IPageableCollection } from "../interfaces/IPageableCollection";
import { IGraphSiteSearchResult, IHit, IHitResource, IHitsContainer } from "../interfaces/IGraphSearchQueryResult";
import { DateService } from "./dateService";
import { IOneDriveRootDetails } from "../interfaces/IOneDriveRootDetails";
import { tryDecodeUriComponent, tryEncodeUriComponent } from "../../../utilities/EncodeDecode";
import { detectIEOrEdge } from "../../../utilities/BrowserUtils";
import { ISharePointIds } from "../interfaces/ISharePointIds";
import { discardCheckOut, getAccessibleSites } from "./SharePointRestServices";
import { IGraphCreateShareableLinkRequest } from "../components/_v2/redux/api/graph/api-graph-interfaces";
import { getAuthenticatedClient } from "./GraphClientService";
import { encodeSharingUrl } from "./sharingUrlToken";
import { ITeamSiteResolution, resolveTeamSites } from "./TeamSiteResolver";
import { getLocalStorage, localStorageKeys, setLocalStorage } from "../components/helpers/localStorageHelper";
import { SharePointSiteHelper } from "../../../utilities/SharePointSiteHelper";
import { apiConvertFileToPdf, apiRemoveProvisionedSites } from "./ApiService";
import { CacheHelper } from "../../../utilities/CacheHelper";
import { IPerson } from "../interfaces/IPersonResult";

const encodePath = (path: string): string => {
  try {
    let pathParts = path.split("/");
    for (let index = 0; index < pathParts.length; index++) {
      let urlPart = pathParts[index];
      pathParts[index] = tryEncodeUriComponent(tryDecodeUriComponent(urlPart.trim()));
    }
    const encodedPath = pathParts.join("/");
    if (!encodedPath?.startsWith("/")) {
      return `/${encodedPath}`;
    }
    return encodedPath;
  } catch (error) {
    logger(error, "GraphService.ts (encodePath)");
  }
  return path;
}

export const getUserProfile = async (select?: string) => {
  try {
    const client = getAuthenticatedClient(await authModule.getGraphAccessToken());
    let req = client
      .api("/me");

    if (select) {
      req = req.select(select);
    }

    return await req.get();
  }
  catch (e) {
    logger(e, "GraphService.ts (getUserProfile)");
  }
  return null;
}

export const getUserProfilePicture = async (accessToken: string) => {
  try {
    const client = getAuthenticatedClient(accessToken);
    const response = await client
      .api("me/photos/48x48/$value")
      .header("Cache-Control", "no-cache")
      .responseType(ResponseType.RAW)
      .get();

    if (response.ok) {
      const url = window.URL || window.webkitURL;
      const blob = await response.blob();
      const imageUrl = url.createObjectURL(blob);
      return imageUrl;
    }
  }
  catch (e) {
    logger(e, "GraphService.ts (getUserProfilePicture)");
  }
  return null;
}

export const getDriverFolders = async (pager: IGraphPager, accessToken: string): Promise<IFolderDrivePaging> => {
  try {
    const client = getAuthenticatedClient(accessToken);
    let response = !pager.nextLink || pager.nextLink.trim().length === 0
      ? await client.api("/me/drive/root/children").filter(filters.onlyFolders).expand("listItem($select=webUrl)").select("id,name,parentReference,webUrl").get()
      : await client.api(pager.nextLink).get();

    return mapOneDriveFolderData(response);
  } catch (error) {
    logger(error, "GraphService.ts (getDriverFolders)");
    return { folders: [], nextLink: "" };
  }
};

const mapOneDriveFolderData = (result): IFolderDrivePaging => {
  return {
    folders: result?.value.map(data => ({
      id: data.id,
      name: data.name,
      driveId: data.parentReference.driveId,
      webUrl: data.listItem?.webUrl ?? data.webUrl
    })),
    nextLink: result["@odata.nextLink"] ?? ""
  };
};

const mapSharepointFolderData = (result): ISharepointFolderDrivePaging => {
  return {
    folders: result?.value.map(data => ({
      id: data.id,
      name: data.name,
      driveId: data.parentReference.driveId,
      lastModifiedDateTime: data.lastModifiedDateTime,
      contentTypeId: data.listItem?.contentType?.id,
      webUrl: data.listItem?.webUrl,
      sharePointUniqueId: data.sharepointIds?.listItemUniqueId
    })),
    nextLink: result["@odata.nextLink"] ?? ""
  };
};

const getSkipToken = (nextLink: string) => {
  if (!nextLink) return "";

  const skipToken = "$skipToken";
  const index = nextLink.indexOf(skipToken);
  const result = nextLink.substring(index).replace(`${skipToken}=`, "");
  return result;
};

export const getChildDriverFolders = async (
  accessToken: string,
  driveInfo: IDriveInfo,
  pager: IGraphPager
): Promise<IFolderDrivePaging> => {
  try {
    const client = getAuthenticatedClient(accessToken);
    let response = !pager.nextLink || pager.nextLink.trim().length === 0
      ? await client.api(`/drives/${driveInfo.driveId}/items/${driveInfo.id}/children`).filter(filters.onlyFolders).expand("listItem($select=webUrl)").select("id,name,parentReference,webUrl").get()
      : await client.api(pager.nextLink).get();

    return mapOneDriveFolderData(response);
  } catch (error) {
    logger(error, "GraphSerivce.ts (getChildDriverFolders)");
    return { folders: [], nextLink: "" };
  }
};

const executePageIteratorCallback = async (client: Client, response: any, callback: PageIteratorCallback) => {
  let pageIterator = new PageIterator(client, response, callback);
  await pageIterator.iterate();
};

export const getSharePointBaseUrl = async (accessToken: string): Promise<string> => {
  try {
    const client = getAuthenticatedClient(accessToken);
    const baseUrl = (
      await client
        .api("sites/root")
        .select("webUrl")
        .get()
    )?.webUrl;
    return baseUrl;
  } catch (error) {
    logger(error, "GraphSerivce.ts (getSharePointBaseUrl)");
    return null;
  }
};

// export const getSharePointSitesCall = async (accessToken: string, nextPageLink?: string, searchValue?: string, hubSiteIds?: string[]): Promise<IPageableCollection> => {
//   try {
//     const client = getAuthenticatedClient(accessToken);
//     let result: any;
//     if (!nextPageLink) {
//       // const requestPath: string = `/sites?search=${!(searchValue?.length > 0) ? "*" : searchValue + "*"} AND contentclass:STS_Site AND NOT WebTemplate:POLICYCTR AND NOT WebTemplate:POINTPUBLISHINGHUB AND NOT WebTemplate:POINTPUBLISHINGPERSONAL AND NOT WebTemplate:POINTPUBLISHINGTOPIC AND NOT WebTemplate:APPCATALOG AND NOT WebTemplate:SPSPERS AND NOT WebTemplate:TEAMCHANNEL AND NOT WebTemplate:PWA AND NOT WebTemplate:EDISC`;
//       // const hubSiteAssociationExclusion: string = Array.isArray(hubSiteIds) && hubSiteIds.length > 0
//       //   ? hubSiteIds.map(hubSiteId => `-RelatedHubSites:${hubSiteId}`).join(" ")
//       //   : "";
//       // const requestPath: string = `/sites?search=(${!(searchValue?.length > 0) ? "*" : searchValue + "*"} contentclass:STS_Site -WebTemplate:POLICYCTR -WebTemplate:POINTPUBLISHINGHUB -WebTemplate:POINTPUBLISHINGPERSONAL -WebTemplate:POINTPUBLISHINGTOPIC -WebTemplate:APPCATALOG -WebTemplate:SPSPERS  -WebTemplate:TEAMCHANNEL -WebTemplate:PWA -WebTemplate:EDISC ${hubSiteAssociationExclusion}) OR IsHubSite:true`;
//       // const requestPath: string = `/sites?search=${!(searchValue?.length > 0) ? "*" : searchValue + "*"} contentclass:STS_Site -WebTemplate:POLICYCTR -WebTemplate:POINTPUBLISHINGHUB -WebTemplate:POINTPUBLISHINGPERSONAL -WebTemplate:POINTPUBLISHINGTOPIC -WebTemplate:APPCATALOG -WebTemplate:SPSPERS  -WebTemplate:TEAMCHANNEL -WebTemplate:PWA -WebTemplate:EDISC`;
//       const hubSiteAssociationExclusion: string = Array.isArray(hubSiteIds) && hubSiteIds.length > 0
//         ? hubSiteIds.map(hubSiteId => `-DepartmentId:${hubSiteId}`).join(" ")
//         : "";
//       const requestPath: string = `/sites?search=${!(searchValue?.length > 0) ? "*" : searchValue + "*"} contentclass:STS_Site -WebTemplate:POLICYCTR -WebTemplate:POINTPUBLISHINGHUB -WebTemplate:POINTPUBLISHINGPERSONAL -WebTemplate:POINTPUBLISHINGTOPIC -WebTemplate:APPCATALOG -WebTemplate:SPSPERS -WebTemplate:TEAMCHANNEL -WebTemplate:PWA -WebTemplate:EDISC ${hubSiteAssociationExclusion}`;
//       const selectParams: string = "id,displayName,description,webUrl,root";
//       result = await client.api(requestPath).select(selectParams).get();
//     }
//     else {
//       result = await client.api(nextPageLink).get();
//     }

//     let sites: any[] = result?.value?.filter(site => site.displayName?.length > 0) ?? [];
//     let nextLink: string | null = result ? result["@odata.nextLink"] : null;
//     if (nextLink && !nextPageLink && searchValue?.length > 1) {
//       try {
//         let siteCount: number = sites.length;
//         let pageCount: number = 0;
//         while (nextLink && ++pageCount < 3) {
//           result = await client.api(nextLink).get();
//           const extraSites: any[] = result?.value?.filter(site => site.displayName?.length > 0) ?? [];
//           const extraSiteCount: number = extraSites.length;
//           for (let i = 0; i < extraSiteCount; i++) {
//             sites[siteCount++] = extraSites[i];
//           }
//           nextLink = result ? result["@odata.nextLink"] : null;
//         }
//       } catch (error) {
//         logger(error, "GraphSerivce.ts (getSharePointSitesCall: paging loop)");
//       }
//     }

//     return { data: sites, nextPageLink: nextLink };
//   } catch (error) {
//     logger(error, "GraphSerivce.ts (getSharePointSitesCall)");
//     return { data: [], nextPageLink: null };
//   }
// };

// export const getAllowedSites = async (accessToken: string, allowedSiteCollections: string[], searchOnDisplayName: boolean = true, hubSiteIds?: string[]): Promise<IPageableCollection> => {
//   try {
//     let allowedSitesResult: any[];

//     const allowedSites: string[] = searchOnDisplayName
//       ? allowedSiteCollections.map(allowedSite => `(${allowedSite}*)`)
//       : allowedSiteCollections.map(allowedSite => `SiteId:${allowedSite}`);

//     const maxUrlLength: number = 4096;
//     const baseQueryLength: number = 110; // https://graph.microsoft.com/v1.0/sites?search=() AND contentclass:STS_Site&$select=id,displayName,webUrl,root
//     const initialSiteFilterLength = allowedSites.join(" OR ").length;
//     // const hubSiteExclusionLength = Array.isArray(hubSiteIds) && hubSiteIds.length > 0 ? (hubSiteIds.length * 54) + 20 : 0; // (IsHubSite:true OR (...-RelatedHubSites:56edcaea-179b-4831-b399-f79a6ec1cd2c))
//     const hubSiteExclusionLength = Array.isArray(hubSiteIds) && hubSiteIds.length > 0 ? (hubSiteIds.length * 51) : 0; // ...-DepartmentId:56edcaea-179b-4831-b399-f79a6ec1cd2c

//     if ((baseQueryLength + initialSiteFilterLength + hubSiteExclusionLength) > maxUrlLength) {
//       // if ((baseQueryLength + initialSiteFilterLength) > maxUrlLength) {
//       const concatLength: number = 4; // {whitespace}OR{whitespace}
//       let sitesToQuery: string[] = [];
//       let siteFilterLength: number = 0;

//       let siteQueries: any[] = [];
//       for (let i = allowedSites.length; i--;) {
//         let site: string | null = allowedSites[i];
//         if ((siteFilterLength + (sitesToQuery.length * concatLength) + (site?.length ?? 0) + hubSiteExclusionLength) > maxUrlLength) {
//           // if ((siteFilterLength + (sitesToQuery.length * concatLength) + (site?.length ?? 0)) > maxUrlLength) {
//           siteQueries.push(searchAllowedSites(accessToken, [...sitesToQuery], hubSiteIds));
//           siteFilterLength = 0;
//           sitesToQuery = [];
//         }
//         siteFilterLength += site?.length ?? 0;
//         if (site) sitesToQuery.push(site);
//       }

//       if (sitesToQuery.length > 0) {
//         siteQueries.push(searchAllowedSites(accessToken, [...sitesToQuery]));
//       };

//       allowedSitesResult = (await Promise.all(siteQueries)).reduce((arr, row) => arr.concat(row), []);
//     }
//     else {
//       allowedSitesResult = await searchAllowedSites(accessToken, allowedSites, hubSiteIds);
//     }

//     return { data: allowedSitesResult, nextPageLink: null };
//   } catch (error) {
//     logger(error, "GraphSerivce.ts (getAllowedSites)");
//     return { data: [], nextPageLink: null };
//   }
// };

// const searchAllowedSites = async (accessToken: string, siteSearchValues: string[], hubSiteIds?: string[]): Promise<any[]> => {
//   try {
//     const client = getAuthenticatedClient(accessToken);

//     const requestPath: string = `/sites?search=(${siteSearchValues.join(" OR ")}) AND contentclass:STS_Site`;
//     // let requestPath: string = `/sites?search=(${siteSearchValues.join(" OR ")}) AND contentclass:STS_Site`;
//     // if (Array.isArray(hubSiteIds) && hubSiteIds.length > 0) {
//     //   requestPath += ` AND (IsHubSite:true OR (${(hubSiteIds.map(hubSiteId => `-RelatedHubSites:${hubSiteId}`).join(" "))}))`;
//     // }
//     const selectParams: string = "id,displayName,description,webUrl,root";

//     let result = await client.api(requestPath).select(selectParams).get();

//     // return result?.value?.filter(site => site.displayName?.length > 0) ?? [];
//     let sites: any[] = result?.value?.filter(site => site.displayName?.length > 0) ?? [];
//     let siteCount: number = sites.length;
//     if (siteCount > 0) {
//       try {
//         let nextLink: string = result["@odata.nextLink"];
//         while (nextLink) {
//           result = await client.api(nextLink).get();
//           const extraSites: any[] = result?.value?.filter(site => site.displayName?.length > 0) ?? [];
//           const extraSiteCount: number = extraSites.length;
//           for (let i = 0; i < extraSiteCount; i++) {
//             sites[siteCount++] = extraSites[i];
//           }
//           nextLink = result ? result["@odata.nextLink"] : null;
//         }
//       } catch (error) {
//         logger(error, "GraphSerivce.ts (searchAllowedSites: paging loop)");
//       }
//     }
//     return sites;
//   } catch (error) {
//     logger(error, "GraphSerivce.ts (searchAllowedSites)");
//     return [];
//   }
// }

export const getSharePointSites = async (accessToken: string, id: string): Promise<IHit[]> => {
  try {
    const client = getAuthenticatedClient(accessToken);
    const requestBody = {
      requests: [
        {
          entityTypes: ["site"],
          query: { queryString: `${id}` },
        }
      ]
    };

    let sites: IGraphSiteSearchResult = await client.api("/search/query").post(requestBody);
    const returnedSites = sites.value[0].hitsContainers[0].hits;
    return returnedSites;
  } catch (error) {
    logger(error, "GraphSerivce.ts (getSharePointSites)");
    return [];
  }
};

export const getSharePointSiteFromDriveId = async (driveId: string): Promise<{ id: string; webUrl: string; displayName: string; } | null> => {
  if (driveId?.length > 50) {
    try {
      const client = getAuthenticatedClient(await authModule.getGraphAccessToken());
      const driveSharePointIds = await client
        .api(`drives/${driveId}`)
        .select("sharepointIds")
        .get();

      if (driveSharePointIds) {
        // const siteId: string = driveSharePointIds.sharePointIds?.siteId;
        // if (siteId) {
        //   const requestBody = {
        //     requests: [
        //       {
        //         entityTypes: ["site"],
        //         query: { queryString: `(*${siteId}*)` },
        //       }
        //     ]
        //   };

        //   const sitesResult: IGraphSiteSearchResult = await client.api("/search/query").post(requestBody);
        //   const sites: IHit[] = sitesResult?.value[0]?.hitsContainers[0]?.hits;
        //   if (Array.isArray(sites) && sites.length > 0) {
        //     return sites[0];
        //   }
        // }
        const siteUrl: string = driveSharePointIds.sharePointIds?.siteUrl;
        if (siteUrl) {
          return getSiteByWebUrl(siteUrl);
        }
      }
    } catch (error) {
      logger(error, "GraphSerivce.ts (getSharePointSiteFromDriveId)");
    }
  }
  return null;
};

// Whether a 401 on a request sent with this Graph token says the sign-in is being renewed rather than that access
// was refused: the token held has expired, or it is no longer the one that was sent (a refresh landed while the
// request was out, or the caller passed in an older one). Judged against the token sent, not only the one held
// when the error is caught (2026-10-28).
const graphTokenStale = (sentToken: string): boolean => {
  if (authModule.getMinutesToExpiration(TokenResource.Graph) <= 0) {
    return true;
  }
  try {
    return !!sentToken && sentToken !== authModule.getPersistedAuthState()?.graphToken;
  } catch (error) {
    return false;
  }
};

export const getAllSubSites = async (searchRequest: ISiteSearchRequest): Promise<ISiteListLoad> => {
  let outageFailure: boolean = false;
  let graphToken: string;
  try {
    let query: string = siteQueries.subs;
    const excludedHubSitesSpecified: boolean = Array.isArray(searchRequest.hubSiteIds) && searchRequest.hubSiteIds.length > 0;
    const allowedSitesSpecified: boolean = Array.isArray(searchRequest.allowedSites) && searchRequest.allowedSites.length > 0;

    if (excludedHubSitesSpecified) {
      query += " " + searchRequest.hubSiteIds.map(hubSiteId => `-DepartmentId:${hubSiteId}`).join(" ");
    }

    if (allowedSitesSpecified) {
      query += ` (${searchRequest.allowedSites.map(allowedSite => `(${allowedSite}*)`).join(" OR ")})`;
    }

    graphToken = await authModule.getGraphAccessToken();
    const client = getAuthenticatedClient(graphToken);
    const requestBody = {
      requests: [
        {
          entityTypes: ["site"],
          query: {
            queryString: query
          },
          fields: [
            "id",
            "name",
            "displayName",
            "description",
            "webUrl"
          ],
          from: searchRequest.from || 0,
          size: searchRequest.size || 199
        }
      ]
    };

    const result: IGraphSiteSearchResult = await client.api("/search/query").post(requestBody);
    const data = result.value[0].hitsContainers[0];
    const hits: IHit[] = data.hits;

    if (Array.isArray(hits) && hits.length > 0) {
      let siteHits: IHitResource[] = hits
        .filter((v, i, a) => a.findIndex(t => t.hitId === v.hitId) === i)
        .map(hit => hit.resource);

      if (allowedSitesSpecified) {
        siteHits = siteHits.filter(site => searchRequest.allowedSites.some(allowedSite => site.displayName?.trim().toLowerCase() === allowedSite.trim()));
      }

      const sites: ISiteInfo[] = siteHits.map(mapSubSites) ?? [];
      return { sites };
    }
  } catch (error) {
    outageFailure = isOutageFailure(error, graphTokenStale(graphToken));
    logger(error, "GraphSerivce.ts (getAllSubSites)");
  }
  return outageFailure ? { sites: [], outageFailure } : { sites: [] };
}

export const searchSharePointSitesAutoPage = async (searchRequest: ISiteSearchRequest): Promise<ISiteSearchResult> => {
  try {
    let query: string;
    let excludedHubSitesSpecified: boolean;

    if (searchRequest.hubSitesOnly) {
      query = siteQueries.hubs;
    }
    else {
      excludedHubSitesSpecified = Array.isArray(searchRequest.hubSiteIds) && searchRequest.hubSiteIds.length > 0;
      if (excludedHubSitesSpecified) {
        query = siteQueries.top + " " + searchRequest.hubSiteIds.map(hubSiteId => `-DepartmentId:${hubSiteId}`).join(" ");
      }
      else {
        query = searchRequest.query?.length > 0
          ? siteQueries.all
          : siteQueries.top;
      }
    }

    const allowedSitesSpecified: boolean = Array.isArray(searchRequest.allowedSites) && searchRequest.allowedSites.length > 0;
    if (allowedSitesSpecified) {
      query += ` (${searchRequest.allowedSites.map(allowedSite => `(${allowedSite}*)`).join(" OR ")})`;
    }

    if (searchRequest.query?.length > 0) {
      query += " " + searchRequest.query.replaceAll('"', '\"');
    }

    const client = getAuthenticatedClient(await authModule.getGraphAccessToken());

    const requestBody = {
      requests: [
        {
          entityTypes: ["site"],
          query: {
            queryString: query
          },
          from: searchRequest.from || 0,
          size: searchRequest.size || 199
        }
      ]
    };

    let sites: ISiteInfo[] = [];
    let data: IHitsContainer;
    let uniqueSiteCount: number = 0;
    let pageCount: number = 1;

    do {
      const result: IGraphSiteSearchResult = await client.api("/search/query").post(requestBody);
      data = result.value[0].hitsContainers[0];
      const hits: IHit[] = data.hits;

      if (Array.isArray(hits) && hits.length > 0) {
        let siteHits: IHitResource[] = hits
          .filter((v, i, a) => a.findIndex(t => t.hitId === v.hitId) === i)
          .map(hit => hit.resource);

        uniqueSiteCount += siteHits.length;

        if (allowedSitesSpecified) {
          siteHits = siteHits.filter(site => searchRequest.allowedSites.some(allowedSite => site.displayName?.trim().toLowerCase() === allowedSite.trim()));
        }

        sites = sites.concat(siteHits.map(mapSites) ?? []);

        if (!excludedHubSitesSpecified && SharePointSiteHelper.hubSites.length > 0) {
          sites.forEach(site => {
            const hub: ISiteInfo = SharePointSiteHelper.hubSites.find(hubSite => hubSite.id === site.id);
            if (hub) {
              site.isHub = true;
              site.hubId = hub.hubId;
              if (hub.name) site.name = hub.name;
              if (hub.description) site.description = hub.description;
              if (hub.parentHubId) site.parentHubId = hub.parentHubId;
            }
          });
        }

        requestBody.requests[0].from += siteHits.length;
      }
    } while (data?.moreResultsAvailable === true && ++pageCount < 3);

    return {
      sites: sites,
      pageData: {
        moreResultsAvailable: (data as IHitsContainer).moreResultsAvailable === true,
        from: (searchRequest.from || 0) + uniqueSiteCount
      }
    }
  } catch (error) {
    logger(error, "GraphSerivce.ts (searchSites)");
  }

  return {
    sites: [],
    pageData: initialGraphSearchPager
  }
}

export const searchSharePointSites = async (searchRequest: ISiteSearchRequest): Promise<ISiteSearchResult> => {
  const allowedSitesSpecified: boolean = Array.isArray(searchRequest?.allowedSites) && searchRequest.allowedSites.length > 0;
  let query: string;
  let outageFailure: boolean = false;
  let graphToken: string;

  try {
    let hubSitesSpecified: boolean;

    if (searchRequest.hubSitesOnly) {
      query = siteQueries.hubs;
    }
    else {
      hubSitesSpecified = Array.isArray(searchRequest.hubSiteIds) && searchRequest.hubSiteIds.length > 0;
      if (hubSitesSpecified) {

        if (searchRequest.hubChildrenOnly) {
          // query = siteQueries.top + ` (${searchRequest.hubSiteIds.map(hubSiteId => `(DepartmentId:{${hubSiteId}} OR DepartmentId:${hubSiteId} OR (IsHubSite:true AND RelatedHubSites:${hubSiteId}))`).join(" OR ")})`;
          query = siteQueries.top + ` (${searchRequest.hubSiteIds.map(hubSiteId => `DepartmentId:${hubSiteId}`).join(" OR ")})`;
        }
        else {
          query = siteQueries.top + " " + searchRequest.hubSiteIds.map(hubSiteId => `-DepartmentId:${hubSiteId}`).join(" ");
        }

      }
      else {
        query = searchRequest.query?.length > 0
          ? siteQueries.all
          : siteQueries.top;
      }
    }

    if (allowedSitesSpecified) {
      query += ` (${searchRequest.allowedSites.map(allowedSite => `(${allowedSite}*)`).join(" OR ")})`;
    }

    if (searchRequest.query?.length > 0) {
      query += " " + searchRequest.query.replaceAll('"', '\"');
    }

    let sites: ISiteInfo[] = [];
    let moreResultsAvailable: boolean = true;
    let from: number = searchRequest.from || 0;
    let pageCount: number = 0;
    const pageLimit: number = searchRequest.pageLimit || 1;

    graphToken = await authModule.getGraphAccessToken();
    const client = getAuthenticatedClient(graphToken);

    do {
      const requestBody = {
        requests: [
          {
            entityTypes: ["site"],
            query: {
              queryString: query
            },
            from: from,
            size: searchRequest.size || 199
          }
        ]
      };

      const result: IGraphSiteSearchResult = await client.api("/search/query").post(requestBody);
      const data = result.value[0].hitsContainers[0];
      const hits: IHit[] = data?.hits ?? [];

      if (Array.isArray(hits)) {
        let siteHits: IHitResource[] = hits
          .filter((v, i, a) => a.findIndex(t => t.hitId === v.hitId) === i)
          .map(hit => hit.resource);

        const uniqueSiteCount: number = siteHits.length;

        if (allowedSitesSpecified) {
          siteHits = siteHits.filter(site => searchRequest.allowedSites.some(allowedSite => site.displayName?.trim().toLowerCase() === allowedSite.trim()));
        }

        sites = sites.concat(siteHits.map(mapSites)?.filter(s => sites.findIndex(site => s.id === site.id) === -1) ?? []);

        moreResultsAvailable = data.moreResultsAvailable === true;
        from += uniqueSiteCount;
      }
      else {
        moreResultsAvailable = false;
      }
    } while (moreResultsAvailable && ++pageCount < pageLimit);

    if (!searchRequest.skipProvisionedSites && Array.isArray(SharePointSiteHelper.provisionedSites) && SharePointSiteHelper.provisionedSites.length > 0) {
      try {
        const provisionedSitesToBeRemovedFromCache: string[] = [];

        for (const siteId of SharePointSiteHelper.provisionedSites) {
          const siteIdLowerCase: string = siteId.toLowerCase();
          if (SharePointSiteHelper.hubSites.findIndex(hub => hub.id.toLowerCase().indexOf(siteIdLowerCase) > -1) > -1 || sites.findIndex(site => site.id.toLowerCase().indexOf(siteIdLowerCase) > -1) > -1) {
            provisionedSitesToBeRemovedFromCache.push(siteId);
          }
        }

        const remainingProvisionedSites: string[] = SharePointSiteHelper.provisionedSites.filter(s => !provisionedSitesToBeRemovedFromCache.includes(s));
        if (remainingProvisionedSites.length > 0) {
          for (let i = 0; i < remainingProvisionedSites.length; i += 20) {
            const slices: string[] = remainingProvisionedSites.slice(i, i + 20);

            const batchRequests: BatchRequestStep[] = slices.map(siteId => {
              return {
                id: siteId,
                request: new Request(`/sites/${siteId}?$select=id,displayName,description,webUrl`, {
                  method: "GET"
                })
              };
            });

            const content = await new BatchRequestContent(batchRequests).getContent();
            const batchResponse = await client.api("/$batch").post(content);
            const batchResponseContent = new BatchResponseContent(batchResponse);

            await Promise.all(slices.map(async siteId => {
              const siteResponse = batchResponseContent.getResponseById(siteId);
              if (siteResponse.ok) {
                const site = await siteResponse.json();
                if (site) {
                  sites.push({
                    id: site.id,
                    name: site.displayName,
                    description: site.description,
                    webUrl: site.webUrl,
                    siteName: site.displayName,
                    isSite: true,
                    isSubsite: false
                  });
                }
              }
              else if (siteResponse.status === 400
                || siteResponse.status === 401
                || siteResponse.status === 403
                || siteResponse.status === 404
                || siteResponse.status === 410) {
                provisionedSitesToBeRemovedFromCache.push(siteId);
              }
            }));
          }
        }

        if (provisionedSitesToBeRemovedFromCache.length > 0) {
          // Not awaited; rejects when the Docubird API is down (already logged). The next answer still lists these sites,
          // so the next load asks again (2026-10-28).
          apiRemoveProvisionedSites((await authModule.getAccount())?.accountId, provisionedSitesToBeRemovedFromCache).catch(() => undefined);
        }
      } catch (error) {
        // Ignore
      } finally {
        SharePointSiteHelper.clearProvisionedSites();
      }
    }

    if (SharePointSiteHelper.hubSites.length > 0) {
      sites.forEach(site => {
        const hub: ISiteInfo = SharePointSiteHelper.hubSites.find(hubSite => hubSite.id === site.id);
        if (hub) {
          site.isHub = true;
          site.hubId = hub.hubId;
          if (hub.name) site.name = hub.name;
          if (hub.description) site.description = hub.description;
          if (hub.parentHubId) site.parentHubId = hub.parentHubId;
        }
        else if (searchRequest.hubChildrenOnly) {
          site.isHubChild = true;
        }
      });
    }

    return {
      sites: sites,
      pageData: {
        moreResultsAvailable: moreResultsAvailable,
        from: from
      }
    }
  } catch (error) {
    const graphError: GraphError = error as GraphError;
    outageFailure = isOutageFailure(error, graphTokenStale(graphToken));
    // Only a fallback that answers with sites replaces Graph's verdict (a 500/503, so an outage): a refused fallback,
    // or none for an account without a SharePoint address, says nothing about the user's sites (2026-10-28).
    const sharePointBaseUrl: string = !searchRequest?.hubSitesOnly && (graphError?.statusCode === 503 || graphError?.statusCode === 500)
      ? (await authModule.getAccount(false))?.sharePointBaseUrl
      : undefined;
    if (sharePointBaseUrl) {
      try {
        const accessibleSites = await getAccessibleSites(sharePointBaseUrl, query, { rethrow: true });
        if (Array.isArray(accessibleSites)) {
          return {
            sites: allowedSitesSpecified
              ? accessibleSites.filter(site => searchRequest.allowedSites.some(allowedSite => site.name?.trim().toLowerCase() === allowedSite?.trim()))
              : accessibleSites,
            pageData: initialGraphSearchPager
          }
        }
      } catch (fallbackError) {
        // Already logged by getAccessibleSites; Graph's verdict stands.
      }
    }
    logger(error, "GraphSerivce.ts (searchSites)");
  }

  return outageFailure
    ? { sites: [], pageData: initialGraphSearchPager, outageFailure }
    : { sites: [], pageData: initialGraphSearchPager }
}

export const getSharePointSite = async (siteId: string): Promise<ISiteInfo> => {
  try {
    const accessToken = await authModule.getGraphAccessToken();
    const client = getAuthenticatedClient(accessToken);
    const site = await client
      .api(`/sites/${siteId}`)
      .select("id,displayName,webUrl,root")
      .get();

    if (site) {
      return {
        id: site.id,
        name: site.displayName,
        webUrl: site.webUrl,
        isSubsite: !site.root,
      }
    }
  } catch (err) {
    logger(err, "GraphService.ts (getSharePointSite)");
  }
  return null;
};

export const getSharePointSubsite = async (accessToken: string, siteId: string, allowedSites: string[], nextPageLink?: string): Promise<IPageableCollection> => {
  try {
    const client = getAuthenticatedClient(accessToken);
    let result: any;
    if (!nextPageLink) {
      result = await client
        .api(`/sites/${siteId}/sites`)
        .select("id,displayName,description,webUrl")
        .get();
    }
    else {
      result = await client.api(nextPageLink).get();
    }

    let subSites: any[] = result?.value?.filter(site => site.displayName?.length > 0) ?? [];

    let nextLink: string | null = result ? result["@odata.nextLink"] : null;
    if (nextLink?.length > 0) {
      try {
        let subSiteCount: number = subSites.length;
        let pageCount: number = 0;
        while (nextLink && pageCount++ < 5) {
          result = await client.api(nextLink).get();
          const extraSites: any[] = result?.value?.filter(site => site.displayName?.length > 0) ?? [];
          const extraSiteCount: number = extraSites.length;
          for (let i = 0; i < extraSiteCount; i++) {
            subSites[subSiteCount++] = extraSites[i];
          }
          nextLink = result ? result["@odata.nextLink"] : null;
        }
      } catch (error) {
        logger(error, "LocationService.ts (getLocationSubSites: paging loop)");
      }
    }

    if (Array.isArray(allowedSites) && allowedSites.length > 0) {
      subSites = subSites.filter(subSite => allowedSites.some(allowedSite => subSite.displayName?.trim().toLowerCase() === allowedSite.trim()));
    }

    return { data: subSites?.sort((a, b) => a.displayName?.toUpperCase() < b.displayName?.toUpperCase() ? -1 : 1), nextPageLink: nextLink };
  } catch (err) {
    logger(err, "GraphService.ts (getSharePointSubsite)");
    return { data: [], nextPageLink: null };
  }
};

export const getSubSites = async (accessToken: string, mainSites: ISiteInfo[]): Promise<ISubSiteInfo[]> => {
  try {
    const client = getAuthenticatedClient(accessToken);

    const subSiteResults: ISubSiteInfo[] = [];

    for (let i = 0; i < mainSites.length; i += 20) {
      const mainSiteSlices: ISiteInfo[] = mainSites.slice(i, i + 20);

      const batchRequests: BatchRequestStep[] = mainSiteSlices.map(mainSite => {
        return {
          id: mainSite.id,
          request: new Request(`/sites/${mainSite.id}/sites?$select=id,displayName,webUrl`, {
            method: "GET"
          })
        };
      });

      const content = await new BatchRequestContent(batchRequests).getContent();
      const batchResponse = await client.api("/$batch").post(content);
      const batchResponseContent = new BatchResponseContent(batchResponse);

      await Promise.all(mainSiteSlices.map(async mainSite => {
        const subSiteResponse = batchResponseContent.getResponseById(mainSite.id);
        if (subSiteResponse.ok) {
          const subSites = (await subSiteResponse.json())?.value;
          if (subSites) {
            subSites.map(subSite => {
              subSiteResults.push({
                id: subSite.id,
                name: subSite.displayName,
                webUrl: subSite.webUrl,
                parentSiteId: mainSite.id
              });
            })
          }
        }
      }));
    }

    return subSiteResults?.sort((a, b) => a.name?.toUpperCase() < b.name?.toUpperCase() ? -1 : 1);
  } catch (error) {
    // Adding request with duplicate id (Make the id of the requests unique)
    logger(error, "GraphSerivce.ts (getSubSites)");
    return [];
  }
};

// export const getAllowedHubSites = async (allowedSites: string[]): Promise<any[]> => {
//   try {
//     const client = getAuthenticatedClient(await authModule.getGraphAccessToken());

//     const requestPath: string = `/sites?search=contentclass:STS_Site IsHubSite:true`;
//     const selectParams: string = "id,displayName,description,webUrl,root";

//     let result = await client.api(requestPath).select(selectParams).get();

//     let sites: any[] = result?.value?.filter(site => site.displayName?.length > 0) ?? [];
//     let siteCount: number = sites.length;
//     if (siteCount > 0) {
//       let pageCount: number = 0;
//       try {
//         let nextLink: string = result["@odata.nextLink"];
//         while (nextLink && pageCount++ < 5) {
//           result = await client.api(nextLink).get();
//           const extraSites: any[] = result?.value?.filter(site => site.displayName?.length > 0) ?? [];
//           const extraSiteCount: number = extraSites.length;
//           for (let i = 0; i < extraSiteCount; i++) {
//             sites[siteCount++] = extraSites[i];
//           }
//           nextLink = result ? result["@odata.nextLink"] : null;
//         }
//       } catch (error) {
//         logger(error, "GraphSerivce.ts (getAllowedHubSites: paging loop)");
//       }

//       if (Array.isArray(allowedSites) && allowedSites.length > 0) {
//         sites = sites.filter(site => allowedSites.some(allowedSite => site.displayName?.trim().toLowerCase() === allowedSite.trim()));
//       }
//     }

//     return sites;
//   } catch (error) {
//     logger(error, "GraphSerivce.ts (getAllowedHubSites)");
//     return [];
//   }
// }

export const getHubSiteSites = async (accessToken: string, hubSiteId: string, allowedSites: string[]): Promise<any[]> => {
  try {

    const associatedHubSites: ISiteInfo[] = SharePointSiteHelper.hubSites?.filter(hub => hub.parentHubId && hub.parentHubId.toLowerCase() === hubSiteId.toLowerCase());
    const associatedHubSitesAvailable: boolean = Array.isArray(associatedHubSites) && associatedHubSites.length > 0;

    const client = getAuthenticatedClient(accessToken);

    // const requestPath: string = `/sites?search=contentclass:STS_Site AND (DepartmentId:{${hubSiteId}} OR DepartmentId:${hubSiteId} OR RelatedHubSites:${hubSiteId}) -SiteId:${hubSiteId}`;
    const requestPath: string = `/sites?search=(contentclass:STS_Site OR contentclass:STS_Web) AND (DepartmentId:{${hubSiteId}} OR DepartmentId:${hubSiteId} OR (IsHubSite:true AND RelatedHubSites:${hubSiteId})) -SiteId:${hubSiteId}`;
    const selectParams: string = "id,displayName,description,webUrl,root";

    let result = await client.api(requestPath).select(selectParams).get();

    let sites: any[] = result?.value?.filter(site => site.displayName?.length > 0) ?? [];
    let siteCount: number = sites.length;
    if (siteCount > 0 || associatedHubSitesAvailable) {
      let pageCount: number = 0;
      try {
        let nextLink: string;
        if (result) nextLink = result["@odata.nextLink"];
        while (nextLink && pageCount++ < 5) {
          result = await client.api(nextLink).get();
          const extraSites: any[] = result?.value?.filter(site => site.displayName?.length > 0) ?? [];
          const extraSiteCount: number = extraSites.length;
          for (let i = 0; i < extraSiteCount; i++) {
            sites[siteCount++] = extraSites[i];
          }
          nextLink = result ? result["@odata.nextLink"] : null;
        }
      } catch (error) {
        logger(error, "GraphSerivce.ts (getHubSiteSites: paging loop)");
      }

      if (siteCount > 0 && Array.isArray(allowedSites) && allowedSites.length > 0) {
        sites = sites.filter(site => allowedSites.some(allowedSite => site.displayName?.trim().toLowerCase() === allowedSite.trim()));
      }

      if (!Array.isArray(sites)) sites = [];

      if (SharePointSiteHelper.hubSites.length > 0) {
        if (sites.length > 0) {
          sites.forEach(site => {
            const hub: ISiteInfo = SharePointSiteHelper.hubSites.find(hubSite => hubSite.id === site.id);
            if (hub) {
              site.isHub = true;
              site.hubId = hub.hubId;
              if (hub.name) site.displayName = hub.name;
              if (hub.description) site.description = hub.description;
              if (hub.parentHubId) site.parentHubId = hub.parentHubId;
            }
          });
        }

        if (associatedHubSitesAvailable) {
          associatedHubSites.forEach(associatedHub => {
            const hubExists: boolean = sites.some(subSite => subSite.id?.trim().toLowerCase() === associatedHub.id?.trim().toLowerCase());
            if (!hubExists) {
              sites.push({
                ...associatedHub,
                displayName: associatedHub.name
              });
            }
          });
        }
      }
    }

    return sites
      .filter(site => !site.parentHubId || site.parentHubId === hubSiteId)
      .filter(filterNonTopLevelSites)
      .sort((a, b) => a.displayName?.toUpperCase() < b.displayName?.toUpperCase() ? -1 : 1);
  } catch (error) {
    logger(error, "GraphSerivce.ts (getHubSiteSites)");
    return [];
  }
}

export const getSharePointDocumentLibraries = async (
  accessToken: string,
  siteId: string,
  includeLists?: boolean
): Promise<ISharepointDocument[]> => {
  try {
    if (includeLists) {
      return [
        ...await getSharePointLibraries(accessToken, siteId),
        ...await getSharePointLists(accessToken, siteId)
      ];
    }
    else {
      return (await getSharePointLibraries(accessToken, siteId)) ?? [];
    }
  } catch (err) {
    logger(err, "GraphService.ts (getSharePointDocumentLibraries)");
    return [];
  }
};

const getSharePointLibraries = async (
  accessToken: string,
  siteId: string
): Promise<ISharepointDocument[]> => {
  try {
    const client = getAuthenticatedClient(accessToken);
    const libraries = await client
      .api(`/sites/${siteId}/drives`)
      .select("id,name,webUrl,sharepointIds")
      .expand("list($select=displayName)")
      .get();

    const libraryPageIterator = setUpSharePointDocumentPageIterator();
    await executePageIteratorCallback(client, libraries, libraryPageIterator.callback);
    return libraryPageIterator.resultSet?.sort((a, b) => a.name?.toUpperCase() < b.name?.toUpperCase() ? -1 : 1) ?? [];
  } catch (err) {
    logger(err, "GraphService.ts (getSharePointLibraries)");
    return [];
  }
};

const getSharePointLists = async (
  accessToken: string,
  siteId: string
): Promise<ISharepointDocument[]> => {
  try {
    const client = getAuthenticatedClient(accessToken);
    const lists = await client.api(`/sites/${siteId}/lists`).select("id,displayName,webUrl,list,sharepointIds").get();
    const listPageIterator = setUpSharePointListPageIterator();
    await executePageIteratorCallback(client, lists, listPageIterator.callback);
    return listPageIterator.resultSet?.sort((a, b) => a.name?.toUpperCase() < b.name?.toUpperCase() ? -1 : 1) ?? [];
  } catch (err) {
    logger(err, "GraphService.ts (getSharePointLists)");
    return [];
  }
};

const setUpSharePointDocumentPageIterator = () => {
  let resultSet: ISharepointDocument[] = [];
  let callback: PageIteratorCallback = data => {
    // Skip built-in system libraries (e.g. Preservation Hold Library) - matched by url segment,
    // not the localized display name. See systemLibraryHelper.
    if (!isSystemLibrary(data.webUrl, data.sharePointIds?.siteUrl)) {
      resultSet.push({
        id: data.id,
        name: data.list?.displayName ?? data.name,
        webUrl: data.webUrl,
        siteUrl: data.sharePointIds.siteUrl,
        siteId: data.sharePointIds.siteId,
        sharePointListId: data.sharePointIds.listId
      });
    }

    return true;
  };

  return { resultSet, callback };
};

const setUpSharePointListPageIterator = () => {
  let resultSet: ISharepointDocument[] = [];
  let callback: PageIteratorCallback = data => {
    if ((data.list.template === "genericList" || data.list.template === "customGrid") && !data.list.hidden) {
      resultSet.push({
        id: data.id,
        name: data.displayName ?? data.name,
        webUrl: data.webUrl,
        siteUrl: data.sharepointIds.siteUrl,
        siteId: data.sharepointIds.siteId,
        sharePointListId: data.sharepointIds.listId,
        isList: true
      });
    }
    return true;
  };

  return { resultSet, callback };
};

// The sites of a team's private and shared channels, kept for the session. rethrow (Copilot): a
// failed channel list or channel lookup rejects, so Copilot leaves its scope unresolved rather
// than searching the team site alone, and a list that left out a failed channel is never used.
// Without it (search and the libraries at a team root) that short list is kept and used, as
// before Copilot: asking again on every search held each search up and logged each failure again.
// The channel list is always read with rethrow, so a failure is never kept as "no channels"; an
// empty list a caller passes may be one, so it isn't kept (audit 2026-10-19).
// With rethrow, a channel answering 403 or 404 is left out and the list still counts as complete: the
// user can't open its site (a Teams admin lists private channels they aren't a member of) or the site
// is gone, so Copilot couldn't return its files to them either. Other failures reject (review 2026-10-20).
// A 404 can also be a new channel whose site is still being made: left out until reload, with nothing
// indexed there yet (checked 2026-10-20).
export const getTeamChannelRelatedSitesInfo = async (teamGroupId: string, teamDriveId: string, teamChannels?: any[], options: { rethrow?: boolean } = {}): Promise<ITeamChannelDocumentLibrary[]> => {
  try {
    const teamRelatedSiteInfoCacheKey: string = `channelsites.${teamGroupId}.${teamDriveId}`;
    const isShortListCacheKey: string = `channelsites.short.${teamGroupId}.${teamDriveId}`;
    const cachedSiteInfo: ITeamChannelDocumentLibrary[] = CacheHelper.get(teamRelatedSiteInfoCacheKey);
    if (Array.isArray(cachedSiteInfo) && !(options.rethrow && CacheHelper.get(isShortListCacheKey) === true)) {
      return cachedSiteInfo;
    }

    const channelsCacheKey: string = `channels.${teamGroupId}.${teamDriveId}`;
    const givenChannels: ITeamChannelDto[] | undefined = teamChannels?.filter(channel => channel?.isPrivate || channel?.isShared);
    let privateSharedTeamChannels: ITeamChannelDto[] = givenChannels ?? CacheHelper.get(channelsCacheKey);
    // The cached list is Graph's own answer; a caller's empty list may be a failure it swallowed.
    let isKnownList: boolean = givenChannels ? givenChannels.length > 0 : Array.isArray(privateSharedTeamChannels);

    if (!Array.isArray(privateSharedTeamChannels)) {
      privateSharedTeamChannels = (await getTeamChannels(await authModule.getGraphAccessToken(), teamGroupId, teamDriveId, { rethrow: true }))
        .filter(channel => channel?.isPrivate || channel?.isShared);
      CacheHelper.set(channelsCacheKey, privateSharedTeamChannels);
      isKnownList = true;
    }

    const teamRelatedSiteInfo: ITeamChannelDocumentLibrary[] = [];
    let isComplete: boolean = true;
    await Promise.all(
      privateSharedTeamChannels.map(async channel => {
        let teamChannelInfo: ITeamChannelDocumentLibrary;
        try {
          teamChannelInfo = await getTeamChannelInfo({ channelId: channel.id, driveId: teamDriveId, groupId: teamGroupId, isPrivate: channel.isPrivate, isShared: channel.isShared }, options);
        } catch (error) {
          if (error?.statusCode === 403 || error?.statusCode === 404) {
            return;
          }
          throw error;
        }
        if (teamChannelInfo) {
          teamRelatedSiteInfo.push(teamChannelInfo);
        }
        else {
          isComplete = false;
        }
      })
    );
    if (isKnownList) {
      CacheHelper.set(teamRelatedSiteInfoCacheKey, teamRelatedSiteInfo);
      CacheHelper.set(isShortListCacheKey, !isComplete);
    }

    return teamRelatedSiteInfo;
  } catch (error) {
    logger(error, "GraphService.ts (getTeamChannelRelatedSitesInfo)");
    if (options.rethrow) {
      throw error;
    }
  }
  return [];
}

export const getTeamChannelInfo = async (channelInfo: ITeamChannelInfo, options: { rethrow?: boolean } = {}): Promise<ITeamChannelDocumentLibrary> => {
  try {
    const accessToken = await authModule.getGraphAccessToken();
    const client = getAuthenticatedClient(accessToken);

    let teamDriveInfo, teamSharepointInfo;
    if (channelInfo.isPrivate || channelInfo.isShared || !channelInfo.driveId) {
      teamDriveInfo = await GetTeamDriveInfo(client, channelInfo);
      teamSharepointInfo = await GetTeamSharepointInfo(teamDriveInfo.driveId);
    } else {
      [teamDriveInfo, teamSharepointInfo] = await Promise.all([
        GetTeamDriveInfo(client, channelInfo),
        GetTeamSharepointInfo(channelInfo.driveId)
      ]);
    }

    return {
      documentLibraryName: teamSharepointInfo.documentLibraryName,
      sharePointListId: teamSharepointInfo.sharePointListId,
      sharePointSiteUrl: teamSharepointInfo.sharePointSiteUrl,
      driveItemId: teamDriveInfo.driveItemId,
      driveItemWebUrl: teamDriveInfo.driveItemWebUrl,
      driveId: teamDriveInfo.driveId,
      siteId: teamSharepointInfo.siteId
    };
  } catch (error) {
    // With rethrow, getTeamChannelRelatedSitesInfo logs it: one failure, one log (review 2026-10-19).
    if (options.rethrow) {
      throw error;
    }
    logger(error, "GraphService.ts (getTeamChannelInfo)");
    return null;
  }
};

const GetTeamDriveInfo = async (client: Client, channelInfo: ITeamChannelInfo) => {
  const result = await client
    .api(`/teams/${channelInfo.groupId}/channels/${channelInfo.channelId}/filesFolder`)
    .select("id,webUrl,parentReference")
    .get();

  return { driveItemId: result.id, driveItemWebUrl: result.webUrl, driveId: result.parentReference.driveId };
};

const GetTeamChannelDriveId = async (client: Client, channelInfo: ITeamChannelInfo) => {
  const result = await client
    .api(`/teams/${channelInfo.groupId}/channels/${channelInfo.channelId}/filesFolder`)
    .select("parentReference")
    .get();

  return result?.parentReference?.driveId;
};

export const GetTeamSharepointInfo = async (driveId: string) => {
  const accessToken = await authModule.getGraphAccessToken();
  const client = getAuthenticatedClient(accessToken);
  const result = await client
    .api(`/drives/${driveId}`)
    .select("name,sharepointIds")
    .get();

  return {
    documentLibraryName: result.name,
    sharePointListId: result.sharePointIds.listId,
    sharePointSiteUrl: result.sharePointIds.siteUrl
  };
};

export const getSharePointFolders = async (
  pager: IGraphPager,
  accessToken: string,
  siteId: string,
  driveId: string,
  folderId: string,
  isFallbackAttempt: boolean = false
): Promise<ISharepointFolderDrivePaging> => {
  const client = getAuthenticatedClient(accessToken);

  if (pager.nextLink && pager.nextLink.trim().length > 0) {
    try {
      const response = await client.api(pager.nextLink).get();
      return mapSharepointFolderData(response);
    } catch (error) {
      logger(error, "GraphService.ts (getSharePointFolders - nextLink)");
      return { folders: [], nextLink: "" };
    }
  }

  const folderIdentifier = folderId?.length ? folderId : "root";

  const apiUrl = (isFallbackAttempt || !siteId)
    ? `/drives/${driveId}/items/${folderIdentifier}/children`
    : `/sites/${siteId}/drives/${driveId}/items/${folderIdentifier}/children`;

  try {
    const response = await client.api(apiUrl)
      .filter(filters.onlyFolders)
      .select("id,name,parentReference,lastModifiedDateTime,sharepointIds")
      .expand("listItem($select=contentType,webUrl)")
      .get();

    return mapSharepointFolderData(response);
  } catch (error) {
    if (!isFallbackAttempt && siteId) {
      logger(Error(`Primary call with siteId ${siteId} failed. Attempting fallback. Error: ${error.message}`), "GraphService.ts (getSharePointFolders)");
      return getSharePointFolders(pager, accessToken, siteId, driveId, folderId, true);
    }

    const errorContext = isFallbackAttempt ? "fallback" : "primary";
    logger(error, `GraphService.ts (getSharePointFolders - ${errorContext})`);
    return { folders: [], nextLink: "" };
  }
};

export const getTeam = async (teamId: string): Promise<ITeam | undefined> => {
  try {
    const client = getAuthenticatedClient(await authModule.getGraphAccessToken());

    const getTeamSite = async () => {
      try {
        return await client
          .api(`/groups/${teamId}/sites/root`)
          .select("id,webUrl")
          .expand("drive($select=id)")
          .get()
      } catch (error) {
        logger(error, "GraphService.ts (getTeamSite)");
      }
      return undefined;
    }

    const [team, teamSite] = await Promise.all([
      client.api(`/teams/${teamId}`).select("id,displayName").get(),
      getTeamSite()
    ]);

    if (team) {
      return teamSite
        ? { ...team, siteId: teamSite.id, webUrl: teamSite.webUrl, driveId: teamSite.drive.id }
        : { ...team, siteId: team.id, isSharedTeam: true };
    }
  } catch (error) {
    logger(error, "GraphService.ts (getTeam)");
  }
  return undefined;
}

export interface ITeamsLoad {
  teams: ITeam[];
  // 2026-10-27: true only when the team list could not be read for an outage reason
  outageFailure: boolean;
}

export const getJoinedTeams = async (accessToken?: string): Promise<ITeam[]> => (await loadJoinedTeams(accessToken)).teams;

export const loadJoinedTeams = async (accessToken?: string): Promise<ITeamsLoad> => {
  try {
    // const client = getAuthenticatedClient(accessToken);
    // const teams = await client
    //   .api("me/joinedTeams")
    //   .select("id,displayName")
    //   .get();

    const graphToken: string = accessToken ?? await authModule.getGraphAccessToken();
    const client = getAuthenticatedClient(graphToken);

    let teams;
    let useFallback: boolean = true;

    const teamsToken = await authModule.getTeamsAccessToken();
    if (teamsToken) {
      try {
        const teamsClient = getAuthenticatedClient(await authModule.getTeamsAccessToken());
        teams = await teamsClient
          .api("/me/teamwork/associatedTeams")
          .select("id,displayName")
          .get();

        useFallback = false;
      } catch (associatedTeamsError) {
        logger(associatedTeamsError, "GraphSerivce.ts (getAssociatedTeams)");
      }
    }

    if (useFallback) {
      try {
        teams = await client
          .api("/me/joinedTeams")
          .select("id,displayName")
          .get();
      } catch (joinedTeamsError) {
        logger(joinedTeamsError, "GraphSerivce.ts (getJoinedTeams)");
        // joinedTeams runs only when associatedTeams was not asked or failed, so it alone decides: a refused
        // associatedTeams is no answer about the list and must not turn this outage into an empty one (2026-10-28).
        return { teams: [], outageFailure: isOutageFailure(joinedTeamsError, graphTokenStale(graphToken)) };
      }
    }

    // associatedTeams can return the same team twice (member + shared-channel member);
    // duplicate ids inside a single $batch fail the whole batch with a 400.
    const joinedTeams: ITeam[] = [];
    const seenTeamIds = new Set<string>();
    for (const team of (teams?.value ?? []) as ITeam[]) {
      if (team?.id && !seenTeamIds.has(team.id)) {
        seenTeamIds.add(team.id);
        joinedTeams.push(team);
      }
    }

    // A group's root site (siteId/webUrl/driveId) never changes, so previously resolved
    // teams are served from the per-account cache and only unseen teams cost Graph
    // requests. Every inner $batch request counts individually against SharePoint's
    // throttling quotas, so for users in thousands of teams this is the difference
    // between one request and hundreds per refresh.
    const storedResolutions = getLocalStorage(localStorageKeys.teamSiteResolutions);
    const siteResolutions: Record<string, ITeamSiteResolution> =
      storedResolutions && typeof storedResolutions === "object" && !Array.isArray(storedResolutions) ? storedResolutions : {};

    const outcomeByTeamId = new Map<string, ITeam>();
    const teamsToResolve: ITeam[] = [];

    for (const team of joinedTeams) {
      const cachedResolution = siteResolutions[team.id];
      if (cachedResolution?.siteId && cachedResolution.webUrl && cachedResolution.driveId) {
        outcomeByTeamId.set(team.id, { ...team, siteId: cachedResolution.siteId, webUrl: cachedResolution.webUrl, driveId: cachedResolution.driveId });
      }
      else {
        teamsToResolve.push(team);
      }
    }

    let cacheDirty = false;

    if (teamsToResolve.length > 0) {
      const { resolved, unresolved } = await resolveTeamSites(client, teamsToResolve);

      for (const team of resolved) {
        outcomeByTeamId.set(team.id, team);
        // Only complete, successful resolutions are persisted. The shared-team fallback
        // (403/404) is re-attempted on every refresh: such a response can be transient
        // (e.g. a team whose site is still provisioning) and must not be cached as fact.
        if (!team.isSharedTeam && team.siteId && team.webUrl && team.driveId) {
          siteResolutions[team.id] = { siteId: team.siteId, webUrl: team.webUrl, driveId: team.driveId };
          cacheDirty = true;
        }
      }

      if (unresolved.length > 0) {
        // Throttled or failed past the retry budget: excluded from the result rather
        // than returned with a fabricated siteId, so nothing downstream caches bad data.
        // They reappear on the next refresh, which resolves only what is still missing.
        logger(new Error(`Team site resolution failed for ${unresolved.length} team(s); excluded until the next refresh`), "GraphService.ts (getJoinedTeams)");
      }
    }

    for (const teamId of Object.keys(siteResolutions)) {
      if (!seenTeamIds.has(teamId)) {
        delete siteResolutions[teamId];
        cacheDirty = true;
      }
    }
    // Rewriting is skipped when nothing changed: the store encrypts on write, which is
    // measurable for thousands of entries and would otherwise run on every refresh.
    if (cacheDirty) {
      setLocalStorage(localStorageKeys.teamSiteResolutions, siteResolutions);
    }

    // Emit in the original list order so downstream ordering (the advanced-search site
    // dropdown renders in array order) is identical whether an entry came from the
    // cache or a live resolution - and identical to the pre-cache behavior.
    const teamResults: ITeam[] = [];
    for (const team of joinedTeams) {
      const outcome = outcomeByTeamId.get(team.id);
      if (outcome) {
        teamResults.push(outcome);
      }
    }

    return { teams: teamResults, outageFailure: false };
  } catch (error) {
    logger(error, "GraphSerivce.ts (getJoinedTeams)");
    return { teams: [], outageFailure: false };
  }
};

export const getTeamChannels = async (
  accessToken: string,
  groupId: string,
  driveId: string,
  options: { rethrow?: boolean } = {}
): Promise<ITeamChannelDto[]> => {
  try {
    const client = getAuthenticatedClient(accessToken);

    const [teamChannels, primaryChannel] = await Promise.all([
      client.api(`/teams/${groupId}/channels`).header("Prefer", "include-unknown-enum-members").get(),
      getTeamPrimaryChannel(accessToken, groupId)
    ]);

    if (Array.isArray(teamChannels?.value)) {
      const channels: ITeamChannelDto[] = (teamChannels.value as ITeamChannel[]).map(
        teamChannel =>
          <ITeamChannelDto>{
            id: teamChannel.id,
            displayName: teamChannel.displayName,
            description: teamChannel.description,
            email: teamChannel.email,
            webUrl: teamChannel.webUrl,
            teamGroupId: groupId,
            driveId: driveId,
            isPrivate: teamChannel.membershipType === "private",
            isShared: teamChannel.membershipType === "shared",
            isPrimary: teamChannel.id === primaryChannel?.id
          }
      );

      return [
        ...channels.filter(x => x.isPrimary),
        ...channels
          .filter(x => !x.isPrimary)
          .sort((channelA, channelB) => channelA.displayName?.toUpperCase() < channelB.displayName?.toUpperCase() ? -1 : 1)
      ];
    }
  } catch (error) {
    // With rethrow, getTeamChannelRelatedSitesInfo logs it: one failure, one log (review 2026-10-19).
    if (options.rethrow) {
      throw error;
    }
    logger(error, "GraphSerivce.ts (getTeamChannels)");
  }
  return [];
};

export const getTeamPrimaryChannel = async (accessToken: string, groupId: string) => {
  try {
    const client = getAuthenticatedClient(accessToken);
    return await client.api(`/teams/${groupId}/primaryChannel`).select("id").get();
  } catch (error) {
    logger(error, "GraphService.ts (getTeamPrimaryChannel)");
  }
  return null;
}

export const getTeamsChannelSites = async (teams: ITeam[]) => {
  const teamsChannelSites: ITeamChannelSite[] = [];
  if (Array.isArray(teams)) {
    try {
      for (let i = 0; i < teams.length; i += 10) {
        const teamsSlices: ITeam[] = teams.slice(i, i + 10);

        await Promise.all(teamsSlices.map(async team => {
          const teamChannelSites: ITeamChannelSite[] = await getTeamChannelSites(team.id, teamsSlices.length > 2 ? 2 : 10);
          if (Array.isArray(teamChannelSites))
            for (let x = 0; x < teamChannelSites.length; x++) {
              teamsChannelSites.push({ ...(teamChannelSites[x]), teamSiteId: team.siteId });
            }
        }));
      }
    } catch (error) {
      logger(error, "GraphService.ts (getTeamsChannelSites)");
    }
  }
  return teamsChannelSites;
}

export const getTeamChannelSites = async (teamId: string, parallelLimit: number = 10): Promise<ITeamChannelSite[]> => {
  try {
    const accessToken: string = await authModule.getGraphAccessToken();
    const client = getAuthenticatedClient(accessToken);

    const channelsRaw = (await client.api(`/teams/${teamId}/channels`).header("Prefer", "include-unknown-enum-members").get())?.value;

    const channels: ITeamChannelDto[] = Array.isArray(channelsRaw) && channelsRaw.map(
      channel =>
        <ITeamChannelDto>{
          id: channel.id,
          displayName: channel.displayName,
          description: channel.description,
          email: channel.email,
          webUrl: channel.webUrl,
          teamGroupId: teamId,
          driveId: null,
          isPrivate: channel.membershipType === "private",
          isShared: channel.membershipType === "shared",
          isPrimary: false
        }
    );

    if (Array.isArray(channels)) {
      const privateAndSharedChannels = channels.filter(channel => channel.isPrivate || channel.isShared);

      const channelSiteResults: ITeamChannelSite[] = [];

      for (let i = 0; i < privateAndSharedChannels.length; i += parallelLimit) {
        const channelSlices: ITeamChannelDto[] = privateAndSharedChannels.slice(i, i + parallelLimit);

        await Promise.all(channelSlices.map(async channel => {
          const teamDriveInfo = await GetTeamDriveInfo(client, { channelId: channel.id, groupId: teamId, isPrivate: channel.isPrivate, isShared: channel.isShared });
          const siteInfo = await client
            .api(`/drives/${teamDriveInfo.driveId}`)
            .select("id")
            .expand("list($select=parentReference,sharePointIds)")
            .get();

          channelSiteResults.push({
            id: siteInfo?.list?.parentReference?.siteId,
            displayName: channel.displayName,
            webUrl: siteInfo?.list?.sharepointIds?.siteUrl
          });
        }));
      }

      return channelSiteResults;
    }
  } catch (error) {
    logger(error, "GraphService.ts (getTeamChannelSites)");
  }
  return [];
}

export const getDriveItemDocuments = async (
  accessToken: string,
  searchParams: ISearchDocumentLibraryParams
): Promise<DriveItemSearchResultComposite> => {
  try {
    const searchHelper = new GraphSearchRestHelper(microsoftGraph.url.search, new SearchResultMapper());
    let documentFiles = await searchHelper
      .setBody(searchParams)
      .setHeader(accessToken)
      .Execute();

    return documentFiles;
  } catch (err) {
    logger(err, "GraphService.ts (getDriveItemDocuments)");
    return null;
  }
};

export const getDriveItemMetadata = async (
  documentFiles: DriveItemSearchResult[],
  accessToken: string,
  driveId: string
): Promise<IBatchSearchResult[]> => {
  try {
    const client = getAuthenticatedClient(accessToken);
    const searchBatchHelper = new SearchBatchRequestHelper(client);
    const batchRequestCallback = (item: DriveItemSearchResult, index: number) => ({
      id: index.toString(),
      request: new Request(`/drives/${driveId}/items/${item.id}/listitem/fields?expand=PersonSingleCol`, {
        method: "GET"
      })
    });

    return await searchBatchHelper.process(
      documentFiles.map(item => ({ ...item, driveId: driveId })),
      batchRequestCallback
    );
  } catch (err) {
    logger(err, "GraphService.ts (getDriveItemMetaData)");
    return [];
  }
};

export const getSiteDocumentLibraryDrives = async (
  accessToken: string,
  siteInfos: ISiteBatchInfo[]
): Promise<ISiteDriveItems[]> => {
  // The batch was sent with the token the caller passed in, so a 401 is judged against that one (2026-10-28).
  const tokenStale = (): boolean => graphTokenStale(accessToken);
  try {
    const client = getAuthenticatedClient(accessToken);
    const driveIdBatchHelper = new DriveIdBatchHelper(client, tokenStale);
    return await driveIdBatchHelper.process(siteInfos);
  } catch (err) {
    logger(err, "GraphService.ts (getSiteDocumentLibraryDrives)");
    // No answer for any site: mark each one so none is remembered as having no libraries (2026-10-27).
    if (isOutageFailure(err, tokenStale())) {
      return siteInfos.map(siteWithoutAnswer);
    }
    return [];
  }
};

export const searchMainDriverFolder = async (accessToken: string, searchTerm: string): Promise<any[]> => {
  try {
    const client = getAuthenticatedClient(accessToken);
    const query = client.api(`/me/drive/root/search(q='${searchTerm}')`);
    const result = await executeSearch(query);
    return result?.value ?? [];
  } catch (err) {
    logger(err, "GraphService.ts (searchMainDriverFolder)");
    return [];
  }
};

export const searchChildDriveFolders = async (
  accessToken: string,
  driveInfo: IDriveInfo,
  searchTerm: string
): Promise<any[]> => {
  try {
    const client = getAuthenticatedClient(accessToken);
    const query = client.api(`/drives/${driveInfo.driveId}/items/${driveInfo.id}/search(q='${searchTerm}')`);
    const result = await executeSearch(query);
    return result?.value ?? [];
  } catch (err) {
    logger(err, "GraphService.ts (searchChildDriverFolders)");
    return [];
  }
};

export const searchDriveForFilesAndFolders = async (searchParams: IFileFolderSearch) => {
  try {
    const accessToken = await authModule.getGraphAccessToken();
    const client = getAuthenticatedClient(accessToken);
    const url = searchParams.isRoot
      ? `/drives/${searchParams.driveId}/root/search(q='${searchParams.searchTerm}')?$top=250`
      : `/drives/${searchParams.driveId}/items/${searchParams.folderId}/search(q='${searchParams.searchTerm}')?$top=250`;

    const result =
      (
        await client
          .api(url)
          .select("id,name,parentReference,createdDateTime,lastModifiedDateTime,webDavUrl,lastModifiedBy,file,folder,size")
          .get()
      )?.value ?? [];
    const files = result.filter(item => item.file).map(item => buildOneDriveFiles(item, searchParams.webUrl));
    const folders = result
      .filter(item => item.folder && (searchParams.isRoot ? true : item.id !== searchParams.folderId))
      .map(item => buildOneDriveFolders(item, searchParams.webUrl));
    return { files: files, folders: folders };
  } catch (err) {
    logger(err, "GraphService.ts (searchDriveForFilesAndFolders)");
    return { files: [], folders: [] };
  }
};

const buildOneDriveFiles = (item: any, parentWebUrl: string) => {
  return {
    id: item.id,
    name: item.name,
    driveId: item.parentReference.driveId,
    createdDateTime: item.createdDateTime,
    lastModifiedDateTime: item.lastModifiedDateTime,
    modifiedBy: item.lastModifiedBy?.user?.displayName,
    fileType: getExtension(item.name),
    size: item.size,
    path: tryDecodeUriComponent(item.webDavUrl?.replace(parentWebUrl, "")),
    isFolder: false
  };
};

const buildOneDriveFolders = (item: any, parentWebUrl: string) => {
  return {
    id: item.id,
    name: item.name,
    driveId: item.parentReference?.driveId,
    lastModifiedDateTime: item.lastModifiedDateTime,
    webUrl: item.webDavUrl,
    path: tryDecodeUriComponent(item.webDavUrl?.replace(parentWebUrl, "")),
    isFolder: true
  };
};

// `throwOnError`: a failed request rejects instead of reading as "no more results", for a caller
// that tells the user its results are partial (a client group's own query of several).
export const searchDriveForFilesAndFoldersSharepoint = async (
  searchParams: IFileFolderSearchSharepointParams,
  mapper: IMapper<IGraphSearchResult>,
  pageSize: number = 30,
  throwOnError: boolean = false
) => {
  try {
    const accessToken = await authModule.getGraphAccessToken();
    const client = getAuthenticatedClient(accessToken);

    const searchResponse = {
      requests: [
        {
          entityTypes: ["driveItem"],
          query: { queryString: searchParams.queryString },
          fields: searchFields.graph,
          from: searchParams.from,
          size: pageSize,
          sortProperties: searchParams.sortProperties ?? []
        }
      ]
    };

    let result = await client.api("/search/query").post(searchResponse);

    const mappedResult = mapper.map(result.value);
    return {
      moreResultsAvailable: mappedResult.moreResultsAvailable,
      driveItems: mappedResult.driveItems.map(item => mapFilesAndFolders(item, searchParams.extraDriveInfo))
    };
  } catch (err) {
    logger(err, "GraphService.ts (searchDriveForFilesAndFoldersSharepoint)");
    if (throwOnError) {
      throw err;
    }
    return { moreResultsAvailable: false, driveItems: [] };
  }
};

const mapFilesAndFolders = (item: DriveItemSearchResult, driveInfo: IBetaSearchExtraDriveInfo) => {
  // const itemExt: string = path.extname(item.name);
  // return item.size > 0 || itemExt ? buildFile(item, itemExt, driveInfo) : buildFolder(item, driveInfo);
  return item.isDocument === true ? buildFile(item, path.extname(item.name), driveInfo) : buildFolder(item, driveInfo);
};

const buildFolder = (item: DriveItemSearchResult, driveInfo: IBetaSearchExtraDriveInfo) => {
  return {
    id: item.id,
    name: item.name,
    driveId: item.driveId,
    lastModifiedDateTime: item.lastModifiedDateTime,
    webUrl: item.webUrl,
    siteId: item.siteId,
    // path: decodeURIComponent(item?.webUrl.replace(driveInfo.siteUrl, "")),
    path: tryDecodeUriComponent(
      driveInfo?.siteUrl ? item.webUrl?.replace(driveInfo.siteUrl, "") : new URL(item.webUrl)?.pathname
    ),
    isFolder: true,
    contentTypeId: item.contentTypeId
  };
};

const buildFile = (item: DriveItemSearchResult, itemExt: string, driveInfo: IBetaSearchExtraDriveInfo) => {
  return {
    id: item.id,
    name: item.name,
    driveId: item.driveId,
    createdDateTime: item.createdDateTime,
    lastModifiedDateTime: item?.lastModifiedDateTime,
    modifiedBy: item?.modifiedBy,
    fileType: itemExt,
    path: tryDecodeUriComponent(
      driveInfo?.siteUrl ? item.webUrl?.replace(driveInfo.siteUrl, "") : new URL(item.webUrl)?.pathname
    ),
    isFolder: false,
    siteUrl: driveInfo?.siteUrl,
    fullWebUrl: item.webUrl,
    size: item.size,
    // pii: {
    //   piiDetected: false,
    //   pii: null
    // },
    rank: item.rank
  };
};

export const getOneDriveRootDetails = async (): Promise<IOneDriveRootDetails> => {
  try {
    const client = getAuthenticatedClient(await authModule.getGraphAccessToken());
    const oneDriveDetails = await client
      .api("/me/drive/root")
      .select("parentReference,webUrl,sharepointIds")
      .get();

    return {
      driveId: oneDriveDetails?.parentReference?.driveId,
      webUrl: oneDriveDetails?.webUrl,
      sharePointSiteUrl: oneDriveDetails?.sharepointIds?.siteUrl,
      sharePointListId: oneDriveDetails?.sharepointIds?.listId,
      sharePointItemId: oneDriveDetails?.sharepointIds?.listItemUniqueId,
    };
  } catch (error) {
    logger(error, "GraphService.ts (getOneDriveRootDetails)");
    return null;
  }
};

export const getParentWebURL = async (fileDriveId: string, itemId: string): Promise<string> => {
  try {
    const client = getAuthenticatedClient(await authModule.getGraphAccessToken());
    let query = await client
      .api(`/drives/${fileDriveId}/items/${itemId}`)
      .select("parentReference")
      .get();

    const { id, driveId } = query?.parentReference;

    query = await client
      .api(`/drives/${driveId}/items/${id}`)
      .select("webUrl")
      .get();

    return query?.webUrl;
  } catch (error) {
    logger(error, "GraphService.ts (getParentWebURL)");
    return null;
  }
};

const executeSearch = async (request: GraphRequest) => {
  return await request.select("name,id,webUrl,parentReference,size").get();
};

export const createRootDriveFolder = async (accessToken: string, folderName: string): Promise<any> => {
  try {
    const url = `me/drive/root/children`;
    return await executeFolderCreation(accessToken, url, folderName);
  } catch (err) {
    logger(err, "GraphService.ts (executeSearch)");
    return null;
  }
};

export const createRootSharepointDriveFolder = async (
  accessToken: string,
  driveId: string,
  siteId: string,
  folderName: string
) => {
  try {
    const url = `/sites/${siteId}/drives/${driveId}/items/root/children`;
    return await executeFolderCreation(accessToken, url, folderName);
  } catch (err) {
    logger(err, "GraphService.ts (createRootSharePointDriveFolder)");
    return null;
  }
};

export const createChildFolder = async (accessToken: string, driveInfo: IDriveInfo, folderName: string) => {
  try {
    const url = `/drives/${driveInfo.driveId}/items/${driveInfo.id}/children`;
    return await executeFolderCreation(accessToken, url, folderName);
  } catch (err) {
    logger(err, "GraphService.ts (createChildFolder)");
    return null;
  }
};

const executeFolderCreation = async (accessToken: string, url: string, folderName: string) => {
  const client = getAuthenticatedClient(accessToken);
  return await client.api(url).post({
    name: folderName,
    folder: {},
    "@microsoft.graph.conflictBehavior": "rename"
  });
};

// Only a 404 means the item is gone. Any other failure (a dropped request, a permissions error)
// answers true, so callers never drop a pin or report a deletion on a failure.
export const checkIfExist = async (url: string, select: string = "id"): Promise<boolean> => {
  try {
    const client = getAuthenticatedClient(await authModule.getGraphAccessToken());
    await client.api(url).select(select).get();
    return true;
  } catch (err) {
    if (err?.statusCode === 404) {
      return false;
    }
    logger(err, "GraphService.ts (checkIfExist)", url);
    return true;
  }
};

export const checkIfSharePointListExist = (listId: string, siteId: string): Promise<boolean> => checkIfExist(`sites/${siteId}/lists/${listId}`);

export const checkIfFileOrFolderExist = (driveInfo: IDriveInfo): Promise<boolean> => checkIfExist(buildFileOrFolderInfoUrl(driveInfo));

export const buildFileOrFolderInfoUrl = (driveInfo: IDriveInfo) => {
  return !driveInfo.id ? `/drives/${driveInfo.driveId}` : `/drives/${driveInfo.driveId}/items/${driveInfo.id}`;
};

export const getFileItemInfo = async (driveInfo: IDriveInfo, filename: string, select: string, expand?: string) => {
  try {
    const result = await getFileResult(driveInfo, filename, select, expand);
    return !result ? null : result;
  } catch (err) {
    logger(err, "GraphService.ts (getFileitemInfo)");
    return null;
  }
};

export const getFileResult = async (driveInfo: IDriveInfo, filename: string, select: string, expand: string) => {
  const client = getAuthenticatedClient(await authModule.getGraphAccessToken());
  const url = buildFileInfoUrl(driveInfo, filename);
  return await executeFileQuery(client, url, select, expand);
};

export const buildFileInfoUrl = (driveInfo: IDriveInfo, filename: string) => {
  return !driveInfo.driveId
    ? `/drives/${driveInfo.id}/root:/${tryEncodeUriComponent(filename)}`
    : `/drives/${driveInfo.driveId}/items/${driveInfo.id}:/${tryEncodeUriComponent(filename)}`;
};

export const getFileItemIdFromRelativePath = async (
  accessToken: string,
  driveId: string,
  serverRelativePath: string,
  encodeRelativePath: boolean = true
): Promise<string> => {
  try {
    const client = getAuthenticatedClient(accessToken);
    const result = await client
      .api(`/drives/${driveId}/root:/${encodeRelativePath === true ? tryEncodeUriComponent(serverRelativePath) : serverRelativePath}`)
      .select("id")
      .get();

    return result?.id;
  } catch (err) {
    logger(err, "GraphService.ts (getFileItemIdFromRelativePath)");
    return null;
  }
};

export const executeFileQuery = async (client: Client, url: string, select: string, expand?: string) => {
  try {
    let req: GraphRequest = client
      .api(url)
      .select(select);

    if (expand) {
      req = req.expand(expand);
    }

    const result = await req.get();

    return result; // will throw file not found exception if the file is not found
  } catch (err) {
    logger(err, "GraphService.ts (executeFileQuery)");
    return null;
  }
};

export const getSharePointDefaultWebUrls = async (accessToken: string): Promise<IDefaultSite[]> => {
  try {
    const batchRequests: BatchRequestStep[] = [];
    const client = getAuthenticatedClient(accessToken);
    const rootSiteIndex = 0;
    const subsiteIndex = 1;
    const defaultRootSiteRequest = {
      id: rootSiteIndex.toString(),
      request: new Request(`/sites/root/drives?$select=webUrl`, { method: "GET" })
    };

    const defaultSubsitesRequest = {
      id: subsiteIndex.toString(),
      request: new Request(`/sites/root/sites?$select=webUrl`, { method: "GET" })
    };

    batchRequests.push(defaultRootSiteRequest);
    batchRequests.push(defaultSubsitesRequest);
    const batchResponseContent = await getBatchResponseContent(client, batchRequests);

    let webUrls: IDefaultSite[] = [];
    for (let index = rootSiteIndex; index <= subsiteIndex; index++) {
      let response = batchResponseContent.getResponseById(index.toString());
      if (response.ok) {
        let json = await response.json();
        json.value.forEach(element => {
          webUrls.push({ webUrl: tryDecodeUriComponent(element.webUrl), isSubsite: index === subsiteIndex });
        });
      }
    }

    return webUrls;
  } catch (err) {
    logger(err, "GraphService.ts (getSharePointDefaultWebUrls)");
    return [];
  }
};

export const getItem = async (accessToken: string, driveId: string, itemId: string, expand?: string): Promise<any> => {
  try {
    const client = getAuthenticatedClient(accessToken);
    const url = !itemId ? `drives/${driveId}` : `/drives/${driveId}/items/${itemId}`;

    let request: GraphRequest = client
      .api(url)
      .select("name,webUrl,sharepointIds");

    if (expand) {
      request = request.expand(expand);
    }

    return await request.get();
  } catch (err) {
    // An item that is not found is gone, which callers handle; a drive that is not found is a fault.
    if (err?.statusCode !== 404 || !itemId) {
      logger(err, "GraphService.ts (getItem)");
    }
    return null;
  }
};

export const getItemCustomSelect = async (accessToken: string, driveId: string, itemId: string, select: string, expand?: string): Promise<any> => {
  try {
    const client = getAuthenticatedClient(accessToken);
    const url = !itemId ? `drives/${driveId}` : `/drives/${driveId}/items/${itemId}`;
    let req = client
      .api(url)
      .select(select);

    if (expand) {
      req = req.expand(expand);
    }

    return await req.get();
  } catch (err) {
    logger(err, "GraphService.ts (getItemCustomSelect)");
    return null;
  }
};

export const getList = async (siteId: string, listId: string) => {
  try {
    const client = getAuthenticatedClient(await authModule.getGraphAccessToken());
    return await client.api(`/sites/${siteId}/lists/${listId}`).select("id,displayName,webUrl,list,sharepointIds").get();
  } catch (error) {
    logger(error, "GraphService.ts (getList)");
  }
  return null;
}

export const getContainerSharePointIds = async (driveId: string, itemId: string): Promise<any> => {
  try {
    const client = getAuthenticatedClient(await authModule.getGraphAccessToken());
    const url = !itemId ? `drives/${driveId}/root` : `/drives/${driveId}/items/${itemId}`;
    return (await client
      .api(url)
      .select("sharepointIds")
      .get())?.sharepointIds;
  } catch (err) {
    logger(err, "GraphService.ts (getContainerSharePointIds)");
    return null;
  }
};

export const getItemWebViewUrl = async (accessToken: string, driveId: string, itemId: string): Promise<any> => {
  try {
    const client = getAuthenticatedClient(accessToken);
    const url = !itemId ? `drives/${driveId}` : `/drives/${driveId}/items/${itemId}`;
    let result = await client
      .api(url)
      .select("webUrl")
      .get();

    return result.webUrl;
  } catch (err) {
    logger(err, "GraphService.ts (getItemWebViewUrl)");
    return null;
  }
};

export const getListItemWebUrl = async (accessToken: string, driveId: string, itemId: string) => {
  try {
    const client = getAuthenticatedClient(accessToken);
    let result = await client
      .api(itemId ? `/drives/${driveId}/items/${itemId}/listItem` : `drives/${driveId}`)
      .select("webUrl")
      .get();

    return result.webUrl;
  } catch (err) {
    logger(err, "GraphService.ts (getListItemWebUrl)");
    return null;
  }
};

export const getSPListItemWebUrl = async (accessToken: string, siteId: string, listId: string, itemId: string) => {
  try {
    const client = getAuthenticatedClient(accessToken);
    let result = await client
      .api(itemId ? `sites/${siteId}/lists/${listId}/items/${itemId}/listItem` : `sites/${siteId}/lists/${listId}`)
      .select("webUrl")
      .get();

    return result.webUrl;
  } catch (err) {
    logger(err, "GraphService.ts (getSPListItemWebUrl)");
    return null;
  }
};

export const getDriveItemBySharingUrl = async (sharingUrl: string) => {
  try {
    const base64Value = window.btoa(sharingUrl) as string;
    let encodedUrl = `u!${base64Value.replace(new RegExp("=" + "([^" + "=" + "]*)$"), "" + "$1")}`;
    encodedUrl = encodedUrl.replaceAll("/", "_");
    encodedUrl = encodedUrl.replaceAll("+", "-");

    const client = getAuthenticatedClient(await authModule.getGraphAccessToken());
    return await client
      .api(`/shares/${encodedUrl}/driveItem`)
      .select("id,name,sharepointIds")
      .get();
  } catch (err) {
    // Ignore
  }
  return null;
};

export const getDriveItemByWebUrl = async (webURL: string, useRequestSiteFallback: boolean = false) => {
  try {
    const encodedUrl = encodeSharingUrl(webURL);

    const client = getAuthenticatedClient(await authModule.getGraphAccessToken());
    const driveItem = await client
      .api(`/shares/${encodedUrl}/driveItem`)
      .select("id,parentReference,file,folder,webUrl")
      .expand("listItem($select=webUrl)")
      .get();

    if (driveItem?.parentReference?.driveId) {
      const listItem = await client
        .api(`/drives/${driveItem.parentReference.driveId}/list`)
        .select("parentReference")
        .get();

      if (listItem?.parentReference?.siteId) {
        return {
          id: driveItem.id,
          driveId: driveItem.parentReference.driveId,
          siteId: listItem.parentReference.siteId,
          file: driveItem.file,
          folder: driveItem.folder,
          webUrl: driveItem.webUrl,
          listItemWebUrl: driveItem.listItem?.webUrl
        }
      }
    }
  } catch (err) {
    if (!useRequestSiteFallback) {
      logger(err, "GraphService.ts (getDriveItemByWebUrl)");
    }
  }

  if (useRequestSiteFallback) {
    return await getSiteByWebUrl(webURL);
  }

  return null;
};

export const getSiteByWebUrl = async (webURL: string) => {
  try {
    const urlObj = new URL(webURL);
    const hostname = urlObj.hostname;
    const relativePath = urlObj.pathname === "/" ? "" : urlObj.pathname;

    // Clean trailing slash for the API call
    const cleanPath = relativePath.endsWith('/') ? relativePath.slice(0, -1) : relativePath;
    const siteApiPath = cleanPath === ""
      ? `/sites/${hostname}:/`
      : `/sites/${hostname}:${cleanPath}`;

    const client = getAuthenticatedClient(await authModule.getGraphAccessToken());

    const site = await client.api(siteApiPath).get();
    if (site.id) {
      return {
        id: site.id,
        driveId: null,
        siteId: site.id,
        file: false,
        folder: true,
        webUrl: site.webUrl,
        listItemWebUrl: null,
        displayName: site.displayName
      }
    }
  } catch (error) {
    logger(error, "GraphService.ts (getSiteByWebUrl)");
  }
  return null;
};

// Upper bound on /drives pages followed per site: a guard against a looping nextLink, far
// above any real site (Graph pages drives by the hundred).
const MAX_SITE_DRIVE_PAGES = 20;

// Raw drive listing for the site at siteUrl (resolved by hostname + server-relative path,
// so no site id lookup is needed), following @odata.nextLink so a site with more than one
// page of libraries comes back complete. Each drive carries its list's displayName: the title
// the tenant's hidden-library settings use. Callers decide whether system libraries stay in.
// A failure is logged and gives [], or rejects when the caller asks for it (rethrow).
const listSiteDrives = async (siteUrl: string, rethrow: boolean = false): Promise<any[]> => {
  try {
    const urlObj = new URL(siteUrl);
    const hostname = urlObj.hostname;
    const relativePath = urlObj.pathname === "/" ? "" : urlObj.pathname;

    // Clean trailing slash for the API call
    const cleanPath = relativePath.endsWith('/') ? relativePath.slice(0, -1) : relativePath;
    const siteApiPath = cleanPath === ""
      ? `/sites/${hostname}/drives`
      : `/sites/${hostname}:${cleanPath}:/drives`;

    const client = getAuthenticatedClient(await authModule.getGraphAccessToken());

    const drives: any[] = [];
    let response = await client
      .api(siteApiPath)
      .select("id,name,description,createdDateTime,lastModifiedDateTime,webUrl,sharepointIds,parentReference")
      .expand("list($select=displayName)")
      .get();

    for (let page = 1; response; page++) {
      if (Array.isArray(response.value)) {
        drives.push(...response.value);
      }

      const nextLink: string = response["@odata.nextLink"];
      if (!nextLink) {
        break;
      }
      if (page >= MAX_SITE_DRIVE_PAGES) {
        logger(new Error(`Stopped listing drives of ${siteUrl} after ${page} pages; the listing is incomplete`), "GraphService.ts (listSiteDrives)");
        break;
      }
      // The next link already carries the original $select.
      response = await client.api(nextLink).get();
    }

    return drives;
  } catch (error) {
    logger(error, "GraphService.ts (listSiteDrives)");
    if (rethrow) {
      throw error;
    }
  }
  return [];
};

// Every document library on the site at siteUrl, name-sorted like every other library
// listing in the add-in, minus the built-in system libraries (Preservation Hold Library etc.)
// and minus anything the consumers could not address anyway (no id / no list id). A Tess
// dossier that names only a site means "all of its libraries". `rethrow`: a failed listing
// rejects instead of giving [] (Copilot's scope must tell "no libraries" from "unknown").
export const getSiteDrives = async (siteUrl: string, options: { rethrow?: boolean } = {}): Promise<any[]> => {
  const drives = await listSiteDrives(siteUrl, options.rethrow === true);

  // Every consumer reads sharePointIds.listId unguarded; one odd drive must not take the
  // whole listing (and with it the whole account/project expand) down.
  const isAddressable = (drive: any): boolean => !!drive?.id && !!drive.sharePointIds?.listId;
  const unaddressable = drives.filter(drive => !isAddressable(drive));
  if (unaddressable.length > 0) {
    const names = unaddressable.map(drive => drive?.name || "(unnamed)").join(", ");
    logger(new Error(`${unaddressable.length} drive(s) on ${siteUrl} skipped: no id or sharePointIds.listId (${names})`), "GraphService.ts (getSiteDrives)");
  }

  return drives
    .filter(isAddressable)
    // Matched by url segment, not the localized display name. See systemLibraryHelper. The
    // listing's own site url stands in when Graph omits sharePointIds.siteUrl on a drive.
    .filter(drive => !isSystemLibrary(drive.webUrl, drive.sharePointIds.siteUrl ?? siteUrl))
    .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" }));
};

export const getSiteDriveByName = async (siteUrl: string, driveName: string) => {
  const targetDriveName: string = driveName?.trim()?.toLowerCase();
  if (!targetDriveName) {
    return null;
  }
  const targetDrive = (await listSiteDrives(siteUrl)).find(drive => drive?.name?.trim()?.toLowerCase() === targetDriveName);
  return targetDrive ?? null;
};

export const getListItemContentTypeId = async (driveId: string, itemId: string): Promise<string | undefined> => {
  try {
    const client = getAuthenticatedClient(await authModule.getGraphAccessToken());
    const result = await client
      .api(`/drives/${driveId}/items/${itemId}/listItem`)
      .select("contentType")
      .get();

    return result?.contentType?.id;
  } catch (err) {
    logger(err, "GraphService.ts (getListItemContentTypeId)");
  }
  return undefined;
};

export const getListItemCustomSelect = async (driveId: string, itemId: string, select: string): Promise<any | undefined> => {
  try {
    const client = getAuthenticatedClient(await authModule.getGraphAccessToken());
    return await client
      .api(`/drives/${driveId}/items/${itemId}/listItem`)
      .select(select)
      .get();
  } catch (err) {
    logger(err, "GraphService.ts (getListItemCustomSelect)");
  }
  return undefined;
};

const getBatchResponseContent = async (client: Client, batchRequests: BatchRequestStep[]) => {
  try {
    const content = await new BatchRequestContent(batchRequests).getContent();
    const batchResponse = await client.api("/$batch").post(content);
    return new BatchResponseContent(batchResponse);
  } catch (err) {
    logger(err, "GraphService.ts (getBatchResponseContent)");
    return null;
  }

};

export const getDriveItemAttachmentLinks = async (
  accessToken: string,
  items: IDriveInfo[]
): Promise<IDriveItemShareableLink[]> => {
  try {
    const client = getAuthenticatedClient(accessToken);

    let batchRequests = items.map(
      (item, index): BatchRequestStep => {
        let request = new Request(`/drives/${item.driveId}/items/${item.id}?select=@microsoft.graph.downloadUrl,name`, {
          method: "GET"
        });
        return { id: index.toString(), request: request };
      }
    );

    const batchResponseContent = await getBatchResponseContent(client, batchRequests);

    return await Promise.all(
      batchRequests.map(async ({ id }) => {
        let response = batchResponseContent.getResponseById(id);
        if (!response.ok) {
          throw new Error("no results found");
        }

        let json = await response.json();
        return { webUrl: json["@microsoft.graph.downloadUrl"], name: json.name };
      })
    );
  } catch (err) {
    logger(err, "GraphService.ts (getDriveItemAttachmentLinks)");
    return [];
  }
};

export const createItemShareableLink = async (request: IGraphCreateShareableLinkRequest, accessToken?: string) => {
  try {
    const token = accessToken || await authModule.getGraphAccessToken();
    const client = getAuthenticatedClient(token);
    const content = {
      type: request.type,
      scope: request.scope,
      recipients: request.recipients?.map(recipient => ({ email: recipient }))
    }
    return await client
      .api(`/drives/${request.driveId}/items/${request.itemId}/createLink`)
      .version("beta")
      .post(content);
  } catch (err) {
    logger(err, "GraphService.ts (createShareableLink)");
    return null;
  }
}

export const getDriveItemShareableLinks = async (
  accessToken: string,
  items: IDriveInfo[],
  isOrganization: boolean
): Promise<IShareableLink[]> => {
  try {
    const client = getAuthenticatedClient(accessToken);
    const createLinkSetting = {
      type: isOrganization ? "edit" : "view",
      scope: isOrganization ? "organization" : "anonymous"
    };

    const results: IShareableLink[] = [];
    for (let i = 0; i < items.length; i += 20) {
      const itemsSliced: IDriveInfo[] = items.slice(i, i + 20);
      let batchRequests = itemsSliced.map((item, index) => {
        let request = new Request(`/drives/${item.driveId}/items/${item.id}/createLink`, {
          method: "POST",
          headers: {
            "Content-type": "application/json"
          },
          body: JSON.stringify(createLinkSetting)
        });

        return { id: index.toString(), request: request, name: item.name, webUrl: item.webUrl };
      });

      const batchResponseContent = await getBatchResponseContent(
        client,
        batchRequests.map(item => ({ id: item.id, request: item.request }))
      );
      const result: IShareableLink[] = await Promise.all(
        batchRequests.map(async ({ id, name, webUrl }) => {
          let response = batchResponseContent.getResponseById(id);
          if (response.ok) {
            let json = await response.json();
            return { url: json.link.webUrl as string, adminHasGivenSiteShareableRightsForUser: true, name: name };
          } else {
            return { url: webUrl, adminHasGivenSiteShareableRightsForUser: false, name: name };
          }
        })
      );
      results.push(...result);
    }

    return results;
  } catch (err) {
    logger(err, "GraphService.ts (getDriveItemShareableLinks)");
    return [];
  }
};

export const getSiteIdWebUrl = async (siteId: string) => {
  const token = await authModule.getGraphAccessToken();
  const client = getAuthenticatedClient(token);
  const url = `/sites/${siteId}`;
  let result = await client
    .api(url)
    .select("webUrl")
    .get();

  return result?.webUrl;
};

export const getFolderPathsInfo = async (driveId: string, paths: string[], isOneDrive: boolean = false) => {
  try {
    const pathResults: any[] = [];
    for (let i = 0; i < paths.length; i += 20) {
      const pathSlices: string[] = paths.slice(i, i + 20);
      let batchRequests = pathSlices.map((path, index) => {
        const isDriveItem: boolean = path?.trim().length > 0;
        let request = new Request(
          `/drives/${driveId}/${isDriveItem ? `root:${path}` : ""}?$select=id,name,webUrl,sharepointIds,parentReference${!isOneDrive && isDriveItem ? "&$expand=listItem($select=contentType,webUrl)" : ""}`,
          {
            method: "GET"
          }
        );
        return { id: index.toString(), request: request };
      });

      const accessToken = await authModule.getGraphAccessToken();
      const client = getAuthenticatedClient(accessToken);
      const batchResponseContent = await getBatchResponseContent(client, batchRequests);

      for (let index = 0; index < pathSlices.length; index++) {
        let pathResponse = batchResponseContent.getResponseById(index.toString());
        if (pathResponse.ok) {
          let response = await pathResponse.json();
          pathResults.push(response);
        }
      }
    }
    return pathResults;
  } catch (err) {
    logger(err, "GraphService.ts (getFolderPathsInfo)");
    return [];
  }
};

export const getSharePointDocumentLibrarySharepointId = async (driveId: string): Promise<any> => {
  try {
    const accessToken = await authModule.getGraphAccessToken();
    const client = getAuthenticatedClient(accessToken);
    var response = await client
      .api(`/drives/${driveId}`)
      .select("sharepointIds")
      .get();

    return response.sharePointIds.listId;
  } catch (err) {
    logger(err, "GraphService.ts (getSharePointDocumentLibrarySharepointId)");
    return null;
  }
};

export const getDriveItemSharepointUniqueId = async (driveId: string, itemId: string): Promise<any> => {
  try {
    const accessToken = await authModule.getGraphAccessToken();
    const client = getAuthenticatedClient(accessToken);
    var response = await client
      .api(`/drives/${driveId}/items/${itemId}`)
      .select("sharepointIds")
      .get();

    return response.sharepointIds.listItemUniqueId;
  } catch (err) {
    logger(err, "GraphService.ts (getDriveItemSharepointUniqueId)");
    return null;
  }
};

export const getRecentFiles = async (limitResult?: boolean): Promise<any> => {
  try {
    const accessToken = await authModule.getGraphAccessToken();
    const client = getAuthenticatedClient(accessToken);
    var response = await client
      .api(`/me/drive/recent`)
      .select("remoteItem")
      .top(limitResult ? 25 : 500)
      .get();

    return response?.value?.filter(item => item.remoteItem?.size > 0)?.map(item => item.remoteItem);
  } catch (err) {
    logger(err, "GraphService.ts (getRecentFiles)");
    return null;
  }
};

export const getSharedFiles = async (): Promise<any> => {
  try {
    const accessToken = await authModule.getGraphAccessToken();
    const client = getAuthenticatedClient(accessToken);
    var response = await client
      .api(`/me/drive/sharedWithMe`)
      .select("remoteItem")
      .get();

    return response?.value?.filter(item => item.remoteItem?.size > 0)?.map(item => item.remoteItem);
  } catch (err) {
    logger(err, "GraphService.ts (getSharedFiles)");
    return null;
  }
};

export const getFilePdfByteContent = async (driveInfo: IDriveInfo, includeFormat: boolean = true): Promise<any> => {
  try {
    const accessToken = await authModule.getGraphAccessToken();
    let result: AxiosResponse;

    if (detectIEOrEdge()) {
      const requestPayload: IDriveItemRequest = {
        accessToken: accessToken,
        driveId: driveInfo.driveId,
        itemId: driveInfo.id
      };

      result = await apiConvertFileToPdf(requestPayload);
    }
    else {
      result = await axiosRetryGet(`https://graph.microsoft.com/v1.0/drives/${driveInfo.driveId}/items/${driveInfo.id}/content${includeFormat ? '?format=pdf' : ''}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        responseType: "blob"
      });
    }

    return result?.data;
  } catch (err) {
    logger(err, "GraphService.ts (getFilePdfByteContent)");
    return null;
  }
};

export const getRelativeFileInfo = async (files: ISharepointRelativeFileInfo[], autoFolderCreation: boolean): Promise<any[]> => {
  const fileInfo: any[] = [];
  try {
    const accessToken = await authModule.getGraphAccessToken();
    const client = getAuthenticatedClient(accessToken);

    if (files?.length > 0) {
      for (let i = 0; i < files.length; i += 20) {
        const fileSlices: ISharepointRelativeFileInfo[] = files.slice(i, i + 20);
        let batchRequests = fileSlices.map(
          (item, index): BatchRequestStep => {
            const path = item.documentLibrary ? buildSharepointPath(item, autoFolderCreation) : buildOneDrivePath(item);
            let request = new Request(`/drives/${item.driveId}/root:${encodePath(path)}`, {
              method: "GET"
            });
            return { id: index.toString(), request: request };
          }
        );

        const batchResponseContent = await getBatchResponseContent(client, batchRequests);

        await Promise.all(
          batchRequests.map(async ({ id }) => {
            let response = batchResponseContent.getResponseById(id);
            if (response.ok) {
              fileInfo.push(await response.json());
            }
          })
        );
      }
    }
  } catch (err) {
    logger(err, "GraphService.ts (getRelativeFileInfo)");
  }
  return fileInfo;
};

const buildSharepointPath = (item: ISharepointRelativeFileInfo, autoFolderCreation: boolean) => {
  const subFolderStartIndex = item.directory?.indexOf("/");
  if (subFolderStartIndex > -1) {
    let driveRootRelativeUrl = item.directory.substring(subFolderStartIndex);
    if (driveRootRelativeUrl?.startsWith("/")) {
      driveRootRelativeUrl = driveRootRelativeUrl.substring(1);
    }

    return `/${driveRootRelativeUrl}/${item.fileName}`;
  }

  if (autoFolderCreation) {
    return `/${DateService.getTodayString()}/${item.fileName}`;
  }

  return `/${item.fileName}`;
};

const buildOneDrivePath = (item: ISharepointRelativeFileInfo) => {
  return item.directory ? `/${tryDecodeUriComponent(item.directory)}/${item.fileName}` : `/${item.fileName}`;
};


export const getProtectionLabels = async (): Promise<IProtectionLabel[]> => {
  try {
    const accessToken = await authModule.getGraphAccessToken();
    const client = getAuthenticatedClient(accessToken);
    var response = await client
      .api(`/me/informationProtection/policy/labels`)
      .version("beta")
      .get();
    const protectionLabels: IProtectionLabel[] = response?.value;
    return protectionLabels;
  } catch (err) {
    if (err.statusCode !== 404) {
      logger(err, "GraphService.ts (getProtectionLabels)");
    }
    return null;
  }
}

export const getFilePreview = async (driveId: string, itemId: string) => {
  try {
    const accessToken = await authModule.getGraphAccessToken();
    const client = getAuthenticatedClient(accessToken);
    var response = await client
      .api(`/drives/${driveId}/items/${itemId}/preview`)
      .post(null);
    const filePreviewURL = response?.getUrl ?? response?.postUrl;
    return filePreviewURL;
  } catch (err) {
    logger(err, "GraphService.ts (getFilePreview)");
  }
}

export const getDriveRootWebUrl = async (driveId: string): Promise<string> => {
  const cacheKey: string = `webUrl_${driveId}`;
  let webUrl: string = CacheHelper.get(cacheKey);
  if (!webUrl) {
    try {
      const client = getAuthenticatedClient(await authModule.getGraphAccessToken());
      const response = await client
        .api(`/drives/${driveId}/root`)
        .select('webUrl')
        .get();
      webUrl = response?.webUrl;
      if (webUrl) {
        CacheHelper.set(cacheKey, webUrl);
      }
    } catch (err) {
      logger(err, "GraphService.ts (getDriveRootWebUrl)");
    }
  }
  return webUrl;
}

// export const GetApprovalPeople = async (accessToken: string, searchString: string) => {
//   const client = getAuthenticatedClient(accessToken);
//   let approvalGroups: IReassignPerson[] = [];
//   await Promise.all([
//     await client
//       .api(`users`)
//       .filter(`startswith(displayName,'${searchString}') or startswith(mail,'${searchString}')`)
//       .top(10)
//       .select("displayName,id,mail")
//       .get(),
//     await client
//       .api(`groups`)
//       .filter(`((securityEnabled eq true) or (groupTypes/any(c:c eq 'Unified'))) and (startsWith(displayName,'${searchString}') or startsWith(mail,'${searchString}'))`)
//       .top(10)
//       .select("displayName,id,mail")
//       .get(),
//   ])
//     .then(values => {
//       values.forEach(val => {
//         approvalGroups = approvalGroups.concat(val.value);
//       });
//     })
//   return approvalGroups;
// };

export const getItemApprovalRequestInfo = async (driveId: string, itemId: string) => {
  try {
    const client = getAuthenticatedClient(await authModule.getGraphAccessToken());
    return await client
      .api(`/drives/${driveId}/items/${itemId}`)
      .select("sharepointIds")
      .expand("listItem($select=webUrl)")
      .get();
  } catch (err) {
    logger(err, "GraphService.ts (getItemApprovalRequestInfo)");
  }
}

export const getItemThumbnail = async (driveId: string, itemId: string) => {
  try {
    const client = getAuthenticatedClient(await authModule.getGraphAccessToken());
    const thumbData = await client
      .api(`/drives/${driveId}/items/${itemId}/thumbnails`)
      .select("large")
      .get();

    if (Array.isArray(thumbData?.value) && thumbData.value.length > 0) {
      return thumbData.value[0].large;
    }
  } catch (err) {
    logger(err, "GraphService.ts (getItemThumbnail)");
  }
  return null;
}

export const uploadLargeFile = async (accessToken: string, fileObject: FileUpload, itemId: string, driveId: string, sharePointIds: ISharePointIds, forceCheckout?: boolean) => {
  try {
    const baseSaveUrl = `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${itemId}`;
    const client = getAuthenticatedClient(accessToken);
    const requestConfig = {
      headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
    };

    if (forceCheckout) {
      try {
        await axiosRetryPost(`${baseSaveUrl}/checkout`, {}, requestConfig, false);
      } catch (error) {
        logger(error, "GraphService.ts (uploadLargeFile) - checkout");
        return { error: error?.message };
      }
    }

    const payload = {
      item: {
        "@microsoft.graph.conflictBehavior": "replace",
      },
    };

    const uploadSession: LargeFileUploadSession = await LargeFileUploadTask.createUploadSession(client, `${baseSaveUrl}/createuploadsession`, payload);
    const task = new LargeFileUploadTask(client, fileObject, uploadSession);

    try {
      const uploadResult = await task.upload();

      if (forceCheckout) {
        try {
          await axiosRetryPost(`${baseSaveUrl}/checkin`, { comment: "" }, requestConfig, false);
        } catch (error) {
          logger(error, "GraphService.ts (uploadLargeFile) - checkin");
          return { error: error?.message }
        }
      }

      return {
        data: uploadResult
      };
    } catch (error) {
      logger(error, "GraphService.ts (uploadLargeFile) - upload");

      try {
        await task.cancel();
        if (forceCheckout) {
          discardCheckOut(await authModule.getSharePointAccessToken(), sharePointIds.siteUrl, sharePointIds.listItemUniqueId);
        }
      } catch (error) {
        logger(error, "GraphService.ts (uploadLargeFile) - cancel task");
      }

      return { error: error?.message };
    }
  }
  catch (e) {
    logger(e, "GraphService.ts (uploadLargeFile)");
    return { error: e?.message };
  }
}

// Module-level cache: a tenant's verified domains are stable for the session, so fetch once.
let cachedOrgVerifiedDomains: string[] | undefined;

/**
 * Returns the tenant's verified domains, lower-cased (e.g. ["docubird.com", "docubird.onmicrosoft.com"]).
 * Used to classify a sharing recipient as internal (domain ∈ verified) vs external.
 *
 * Permission: GET /organization?$select=verifiedDomains is readable with the delegated `User.Read`
 * scope — Microsoft Graph explicitly allows reading id/displayName/verifiedDomains of the organization
 * under User.Read — which Docubird already holds, so NO extra consent is required.
 *
 * Returns [] on any failure so callers can fail open (treat external-ness as unknown rather than block).
 */
export const getOrganizationVerifiedDomains = async (accessToken?: string): Promise<string[]> => {
  if (cachedOrgVerifiedDomains) {
    return cachedOrgVerifiedDomains;
  }
  try {
    const token = accessToken || await authModule.getGraphAccessToken();
    const client = getAuthenticatedClient(token);
    const result = await client.api("/organization").select("id,displayName,verifiedDomains").get();
    const organization = result?.value?.[0];
    const domains: string[] = Array.isArray(organization?.verifiedDomains)
      ? organization.verifiedDomains
        .map((domain: any) => domain?.name?.toLowerCase()?.trim())
        .filter((name: string) => !!name)
      : [];
    if (domains.length > 0) {
      console.log(`GraphService: fetched ${domains.length} verified domains for tenant ${organization?.displayName} (${organization?.id})`);
      console.log(`GraphService: verified domains: ${domains.join(", ")}`);
      cachedOrgVerifiedDomains = domains;
    }
    return domains;
  } catch (err) {
    logger(err, "GraphService.ts (getOrganizationVerifiedDomains)");
    return [];
  }
};

export const searchPeople = async (searchValue: string, allowGroups: boolean, allowContacts: boolean = false): Promise<IPerson[]> => {
  try {
    const filters: string[] = [];
    if (!allowGroups || !allowContacts) {
      filters.push("personType/subclass eq 'OrganizationUser' or personType/subclass eq 'Guest'");
      if (allowGroups) {
        filters.push("personType/subclass eq 'UnifiedGroup'");
      }
      if (allowContacts) {
        filters.push("personType/subclass eq 'ImplicitContact'");
      }
    }

    const client = getAuthenticatedClient(await authModule.getGraphAccessToken());
    let peopleRequest = client.api("/me/people")
      .header("X-PeopleQuery-QuerySources", "Mailbox,Directory")
      .search(`"${searchValue}"`)
      .select("id,displayName,userPrincipalName,scoredEmailAddresses,personType")
      .top(30);

    if (filters.length > 0) {
      peopleRequest = peopleRequest.filter(filters.join(" or "));
    }

    const people = await peopleRequest.get();

    if (Array.isArray(people?.value)) {
      return people.value;
    }
  } catch (error) {
    logger(error, "GraphService.ts (searchPeople)");
  }
  return [];
}

export const searchUsers = async (searchString: string) => {
  try {
    const client = getAuthenticatedClient(await authModule.getGraphAccessToken());
    const usersResult = await client
      .api("/users")
      .header("ConsistencyLevel", "eventual")
      .search(`"displayName:${searchString}" OR "mail:${searchString}" OR "userPrincipalName:${searchString}"`)
      .select("id,displayName,mail,userPrincipalName")
      .orderby("displayName")
      .get();

    if (Array.isArray(usersResult?.value)) {
      return usersResult.value;
    }
  } catch (error) {
    logger(error, "GraphService.ts (searchUsers)");
  }
  return [];
}

export const checkout = async (driveId: string, itemId: string) => {
  const client = getAuthenticatedClient(await authModule.getGraphAccessToken());
  await client
    .api(`/drives/${driveId}/items/${itemId}/checkout`)
    .post(null);
}
